import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import Step3RenderingProvider from "@/components/Step3RenderingProvider";
import Step7ApprovalSummary from "@/components/Step7ApprovalSummary";
import {
  ChevronRight, ChevronLeft, Search, Plus, Trash2, Upload,
  FileText, Check, AlertCircle, Loader2, Printer,
  Home, User, Building2, Stethoscope, ClipboardList, Paperclip, Eye,
  Moon, Sun, Shield, Heart, DollarSign, Activity, Plug, Database, X,
  Sparkles, ShieldAlert, ShieldCheck, HelpCircle, AlertTriangle, Paperclip as AttachIcon
} from "lucide-react";

const STEPS = [
  { num: 1, label: "Verify Coverage" },
  { num: 2, label: "Service Information" },
  { num: 3, label: "Rendering Provider" },
  { num: 4, label: "Clinical Documentation" },
  { num: 5, label: "Add Attachments" },
  { num: 6, label: "Review & Submit" },
  { num: 7, label: "Approval Summary" },
];

// Default lists used when no payer config is loaded
const DEFAULT_AUTH_TYPES = ["Inpatient Authorization", "Outpatient Authorization"];
const DEFAULT_SERVICE_TYPES = [
  { code: "1", label: "Medical Care" },
  { code: "2", label: "Surgical" },
  { code: "4", label: "Diagnostic X-Ray" },
  { code: "5", label: "Diagnostic Lab" },
  { code: "47", label: "Hospital - Inpatient" },
  { code: "48", label: "Hospital - Outpatient" },
];
const DEFAULT_PLACES_OF_SERVICE = ["Office", "Inpatient Hospital", "Outpatient Hospital", "Ambulatory Surgical Center", "Emergency Room", "Home", "Telehealth"];
const ADMISSION_TYPES = ["Emergency", "Urgent", "Elective", "Newborn", "Trauma"];
const LEVELS_OF_CARE = ["Acute", "Sub-Acute", "Skilled Nursing", "Rehabilitation", "ICU", "NICU", "Observation"];
const RELATIONSHIPS = ["Self", "Spouse", "Child", "Other"];
const ROLE_CODES = ["Provider", "Facility"];

// Demo clinical scenarios — chesedmedical Internal Medicine practice
// Each scenario maps to realistic ICD-10 + CPT codes. In production this
// would be replaced by a free-text clinical justification fed into a
// medical-coding NLP model.
const SERVICE_SCENARIOS = [
  {
    key: "mri_lumbar",
    label: "MRI Lumbar Spine — Chronic Low Back Pain with Radiculopathy",
    serviceTypeCode: "4",
    placeOfService: "Outpatient Hospital",
    note: "PATIENT PRESENTATION: 55 y/o male, established patient at Chesed Medical, presents with chronic axial low back pain x 6 months progressively worsening over the past 4 weeks. Pain is rated 7-8/10, aching and burning in quality, radiating from the right lumbar region down the posterolateral aspect of the right thigh and calf into the lateral foot (L5-S1 distribution). Associated with paresthesias and intermittent numbness of the right great toe.\n\nHPI: Symptoms began insidiously after lifting a heavy box at work. Initially intermittent, now constant with exacerbation on sitting >30 minutes and any flexion-based activity. Patient reports waking at night due to pain. Denies bowel/bladder incontinence, saddle anesthesia, or progressive weakness — no red flags for cauda equina at this time.\n\nPMH: HTN (well-controlled on lisinopril 20mg), Type 2 DM (HbA1c 6.8% on metformin), obesity (BMI 31.2). No prior spine surgery.\n\nEXAM: Antalgic gait favoring right leg. Lumbar ROM limited to 30° flexion (normal 80°), extension reproduces pain. Positive straight-leg raise at 45° on the right with reproduction of radicular symptoms. Crossed SLR negative. Deep tendon reflexes 1+ at right Achilles (2+ left), 2+ patellar bilaterally. Decreased pinprick sensation along right S1 dermatome. Motor 4+/5 right plantarflexion, 5/5 elsewhere. Negative Babinski.\n\nCONSERVATIVE TREATMENT TRIAL (6 months): (1) Scheduled NSAIDs — meloxicam 15mg daily x 8 weeks, partial relief. (2) Physical therapy x 12 sessions over 8 weeks — McKenzie protocol, core stabilization. (3) Acetaminophen + tramadol PRN. (4) Activity modification, ergonomic adjustments at work. (5) Therapeutic lumbar epidural steroid injection x 1 (8 weeks ago) — provided 3 weeks of relief. Symptoms recurred.\n\nJUSTIFICATION FOR ADVANCED IMAGING: Per ACR Appropriateness Criteria and Cigna Medical Coverage Policy 0349, MRI lumbar spine without contrast is indicated for chronic low back pain (>6 weeks) with neurological deficits (radiculopathy, dermatomal sensory loss, reflex asymmetry) that has failed ≥6 weeks of conservative management, when surgical or interventional management is being considered. Plain films obtained 4 months ago showed only mild degenerative changes — inadequate for surgical planning.\n\nREQUEST: MRI lumbar spine without contrast (CPT 72148) to evaluate for L4-L5 / L5-S1 disc herniation, neuroforaminal stenosis, or other surgically-correctable pathology. Patient has been referred to neurosurgery and surgical consultation is pending MRI findings.",
    diagnoses: [
      { code: "M54.50", description: "Low back pain, unspecified" },
      { code: "M51.36", description: "Other intervertebral disc degeneration, lumbar region" },
      { code: "M54.16", description: "Radiculopathy, lumbar region" },
      { code: "M51.26", description: "Other intervertebral disc displacement, lumbar region" },
    ],
    procedures: [
      { code: "72148", description: "Magnetic resonance imaging, spinal canal and contents, lumbar; without contrast material", type: "CPT/HCPCS", quantity: "1" },
    ],
  },
  {
    key: "colonoscopy",
    label: "Screening Colonoscopy — High-Risk Family History",
    serviceTypeCode: "1",
    placeOfService: "Ambulatory Surgical Center",
    note: "PATIENT PRESENTATION: 45 y/o male, asymptomatic, presents for preventive screening evaluation. Established patient at Chesed Medical Internal Medicine.\n\nFAMILY HISTORY (KEY): Father diagnosed with colon adenocarcinoma at age 50 (stage IIB, treated with right hemicolectomy + adjuvant chemotherapy, currently in remission x 8 years). Paternal uncle diagnosed with colon cancer at age 62. No known hereditary CRC syndrome (Lynch, FAP) in family — genetic counseling declined by patient at this time. No personal history of inflammatory bowel disease, polyps, or prior endoscopy.\n\nPMH: Otherwise healthy. BMI 27. Exercises 3-4x/week. Non-smoker. Social ETOH (2 drinks/week). Current medications: occasional ibuprofen.\n\nREVIEW OF SYSTEMS: Denies rectal bleeding, hematochezia, melena, change in bowel habits, unintentional weight loss, abdominal pain, anemia symptoms, or family history of polyposis. No iron-deficiency anemia on recent CBC (Hgb 14.8, ferritin 78).\n\nGUIDELINE BASIS: Per the American College of Gastroenterology 2021 guidelines and the U.S. Multi-Society Task Force, individuals with a first-degree relative diagnosed with CRC before age 60 (or ≥2 first-degree relatives at any age) should begin screening colonoscopy at age 40 OR 10 years younger than the youngest affected relative — whichever comes first. This patient meets criteria (father dx age 50 → screen at 40). Patient is now 45 and has not previously been screened.\n\nPRE-PROCEDURE: Counseled on procedural risks (perforation 1:1000, bleeding 1:500, sedation reaction <1:1000), benefits, and alternatives (FIT, Cologuard — less sensitive for high-risk individuals). Patient elected colonoscopy. Bowel prep instructions provided (split-dose PEG-electrolyte solution). Pre-procedure labs reviewed: CBC, BMP, INR — all within normal limits. No anticoagulation. NPO instructions given.\n\nREQUEST: Screening colonoscopy (CPT 45378) with cold/hot snare polypectomy at endoscopist discretion. Anesthesia: monitored anesthesia care (MAC) with propofol. Expected duration 30-45 minutes.",
    diagnoses: [
      { code: "Z12.11", description: "Encounter for screening for malignant neoplasm of colon" },
      { code: "Z80.0", description: "Family history of malignant neoplasm of digestive organs" },
      { code: "Z83.71", description: "Family history of colonic polyps" },
    ],
    procedures: [
      { code: "45378", description: "Colonoscopy, flexible; diagnostic, including collection of specimen(s) by brushing or washing, when performed (separate procedure)", type: "CPT/HCPCS", quantity: "1" },
      { code: "45385", description: "Colonoscopy, flexible; with removal of tumor(s), polyp(s), or other lesion(s) by snare technique (if needed)", type: "CPT/HCPCS", quantity: "1" },
    ],
  },
  {
    key: "cardiac_stress",
    label: "Cardiac Stress Test with Myocardial Perfusion — Stable Angina Workup",
    serviceTypeCode: "1",
    placeOfService: "Outpatient Hospital",
    note: "PATIENT PRESENTATION: 58 y/o male presents with 2-week history of intermittent substernal chest pressure, occurring with moderate exertion (climbing 2 flights of stairs, brisk walking >10 minutes). Pain is described as a 'tight squeezing' sensation, 5/10 intensity, lasting 3-5 minutes, fully relieved by rest. Denies radiation to arm/jaw, diaphoresis, nausea, dyspnea at rest, syncope, or orthopnea. No nocturnal symptoms. No prior episodes.\n\nCARDIOVASCULAR RISK FACTORS: (1) Hypertension x 8 years — controlled on lisinopril 20mg + amlodipine 5mg (BP today 138/82, average home BP 132/78). (2) Hyperlipidemia — LDL 142, HDL 38, TG 168, on atorvastatin 40mg (started 6 months ago). (3) Former smoker — 30 pack-year history, quit 5 years ago. (4) Family history — father had MI at age 62, mother has CAD. (5) Sedentary lifestyle. (6) Pre-diabetes (HbA1c 6.2%). Framingham 10-year ASCVD risk: 18.7% (intermediate-high).\n\nEXAM: HR 78 regular, BP 138/82, RR 14, SpO2 98% RA, BMI 28.4. Heart S1/S2 normal, no murmurs/rubs/gallops. Carotids without bruits. Lungs clear. No JVD, no peripheral edema. Distal pulses 2+ symmetric.\n\nDIAGNOSTIC WORKUP TO DATE: 12-lead resting EKG: NSR, rate 76, no ST-T changes, normal axis, no Q waves. CXR: clear lungs, normal cardiac silhouette. Recent labs: troponin <0.01 (asymptomatic), BNP 42 (normal), TSH normal, BMP normal. Lipid panel as above.\n\nPRE-TEST PROBABILITY: Per the 2021 AHA/ACC Chest Pain Guidelines, this patient has intermediate pre-test probability of obstructive CAD (~30-40%) based on age, sex, symptoms, and risk factors. Exercise capacity is adequate. Resting EKG is interpretable.\n\nJUSTIFICATION: Stress myocardial perfusion imaging (SPECT MPI) with exercise treadmill is the appropriate next-step diagnostic study per ACC/AHA guidelines for intermediate-risk patients with stable angina who can exercise. MPI offers higher sensitivity (~90%) and specificity (~85%) than exercise EKG alone (sensitivity ~68%). Findings will guide further management — either medical optimization vs. invasive coronary angiography.\n\nREQUEST: Exercise stress test with SPECT myocardial perfusion imaging (CPT 78452) and stress echocardiography for wall-motion assessment (CPT 93350). Patient will be instructed to hold beta-blocker (none currently) and caffeine 24h prior. NPO 4h pre-test.",
    diagnoses: [
      { code: "R07.9", description: "Chest pain, unspecified" },
      { code: "I20.9", description: "Angina pectoris, unspecified" },
      { code: "I10", description: "Essential (primary) hypertension" },
      { code: "E78.5", description: "Hyperlipidemia, unspecified" },
      { code: "Z87.891", description: "Personal history of nicotine dependence" },
      { code: "Z82.49", description: "Family history of ischemic heart disease and other diseases of the circulatory system" },
    ],
    procedures: [
      { code: "78452", description: "Myocardial perfusion imaging, tomographic (SPECT) (including attenuation correction, qualitative or quantitative wall motion, ejection fraction by first pass or gated technique, additional quantification, when performed); multiple studies, at rest and/or stress (exercise or pharmacologic) and/or redistribution and/or rest reinjection", type: "CPT/HCPCS", quantity: "1" },
      { code: "93016", description: "Cardiovascular stress test using maximal or submaximal treadmill or bicycle exercise, continuous electrocardiographic monitoring, and/or pharmacological stress; physician supervision only", type: "CPT/HCPCS", quantity: "1" },
      { code: "93350", description: "Echocardiography, transthoracic, real-time with image documentation (2D), includes M-mode recording, when performed, during rest and cardiovascular stress test using treadmill", type: "CPT/HCPCS", quantity: "1" },
    ],
  },
  {
    key: "sleep_study",
    label: "Attended Polysomnography — Suspected Severe OSA",
    serviceTypeCode: "1",
    placeOfService: "Outpatient Hospital",
    note: "PATIENT PRESENTATION: 52 y/o male, established patient, referred for in-laboratory polysomnography for evaluation of suspected obstructive sleep apnea (OSA). Symptoms x 6 months progressively worsening.\n\nCHIEF COMPLAINTS: (1) Excessive daytime sleepiness — falls asleep during meetings, while reading, and reports near-miss MVCs from microsleeps while driving. (2) Loud habitual snoring nightly per spouse — 'rattles the bedroom walls', heard from down the hallway. (3) Witnessed apneic episodes — spouse reports cessation of breathing 20-30 seconds, multiple times per hour, with audible gasping/choking on resumption of breath. (4) Morning headaches 4-5 days/week, dull frontal, resolving by mid-morning. (5) Unrefreshing sleep despite 7-8 hours in bed. (6) Nocturia x 2-3 episodes per night. (7) Decreased libido and erectile dysfunction (new in past year).\n\nSCREENING TOOLS: Epworth Sleepiness Scale (ESS) = 18/24 (severe). STOP-BANG = 7/8 (high risk — Snoring YES, Tired YES, Observed apnea YES, Pressure HTN YES, BMI >35 YES, Age >50 YES, Neck >17in YES, Gender male YES).\n\nPHYSICAL EXAM: BMI 34.2 (Class I obesity). Neck circumference 17.5 inches. Mallampati class III. Crowded oropharynx with enlarged uvula and edematous lateral pharyngeal walls. Tonsils 2+ symmetric. Nasal exam: mild septal deviation, no obstruction. BP 148/92 (newly elevated, previously 130/80). HR 76 NSR.\n\nPMH: Type 2 DM (HbA1c 7.4%, on metformin + empagliflozin), hypertension (likely secondary to undiagnosed OSA), GERD, atrial fibrillation (paroxysmal, on rivaroxaban + diltiazem). Concerns: untreated OSA contributing to refractory HTN and increased Afib burden.\n\nDIFFERENTIAL: Severe OSA most likely. Consider: central sleep apnea (less likely given exam and risk factors), upper airway resistance syndrome, REM sleep behavior disorder, insufficient sleep syndrome (excluded — patient is in bed 8h/night).\n\nJUSTIFICATION: Per AASM 2017 Clinical Practice Guideline, attended in-laboratory polysomnography (Type I sleep study) is the gold standard for diagnosing OSA, particularly in patients with comorbidities (Afib, refractory HTN, T2DM) where home sleep apnea testing (HSAT) has reduced accuracy and false-negative rates. This patient also requires CPAP titration if AHI >15 — most efficiently performed during a split-night study.\n\nREQUEST: Attended in-laboratory polysomnography with sleep staging and 4+ additional parameters (CPT 95810). If AHI ≥20 in the first 2 hours, convert to split-night CPAP titration (CPT 95811). Goals: (1) Confirm OSA diagnosis. (2) Determine severity (AHI, ODI, nadir SpO2). (3) Titrate optimal CPAP pressure if indicated. Patient will return for follow-up within 2 weeks of study.",
    diagnoses: [
      { code: "G47.33", description: "Obstructive sleep apnea (adult) (pediatric)" },
      { code: "R06.83", description: "Snoring" },
      { code: "E66.9", description: "Obesity, unspecified" },
      { code: "R53.83", description: "Other fatigue" },
      { code: "I10", description: "Essential (primary) hypertension" },
      { code: "I48.0", description: "Paroxysmal atrial fibrillation" },
    ],
    procedures: [
      { code: "95810", description: "Polysomnography; age 6 years or older, sleep staging with 4 or more additional parameters of sleep, attended by a technologist", type: "CPT/HCPCS", quantity: "1" },
      { code: "95811", description: "Polysomnography; age 6 years or older, sleep staging with 4 or more additional parameters of sleep, with initiation of continuous positive airway pressure therapy or bilevel ventilation, attended by a technologist (if titration required)", type: "CPT/HCPCS", quantity: "1" },
    ],
  },
  {
    key: "pt_rotator_cuff",
    label: "Physical Therapy — Post-Op Right Rotator Cuff Repair Rehab",
    serviceTypeCode: "PT",
    placeOfService: "Office",
    note: "PATIENT PRESENTATION: 62 y/o right-hand-dominant retired contractor, post-op day 14 from arthroscopic right rotator cuff repair. Procedure performed by Dr. Ramirez (Orthopedic Surgery) at Chesed Surgical Center.\n\nSURGICAL DETAILS: Full-thickness tear of the supraspinatus tendon (2.5 cm retracted tear), with partial-thickness involvement of the infraspinatus. Single-row arthroscopic repair using two suture anchors. Concomitant subacromial decompression and acromioplasty performed. Glenohumeral cartilage intact. Biceps tendon stable. Surgeon's protocol: phase-based rehab over 16 weeks, no active resisted shoulder use for 6 weeks, sling 24/7 for 4 weeks then weaning.\n\nMECHANISM: Insidious onset of right shoulder pain over 8 months, exacerbated by overhead activity (painting, ceiling work). Failed conservative management (6 weeks of formal PT, two subacromial corticosteroid injections, NSAIDs, activity modification). MRI confirmed full-thickness tear. Conservative options exhausted → surgical referral → repair as above.\n\nCURRENT STATUS (POD #14): Patient is in standard immobilizer sling. Surgical incisions well-healed, no signs of infection. Reports controlled pain (3/10 on acetaminophen + occasional tramadol PRN, weaning off narcotics). Significant stiffness already developing. Limited passive ROM: forward flexion 60°, external rotation -10° at side (capsular tightness). Strength testing deferred per protocol.\n\nGOALS OF PHYSICAL THERAPY: (1) Restore full passive range of motion by week 6. (2) Restore active assisted ROM by week 8. (3) Progressive resistance training initiated at week 12. (4) Return to functional ADLs (dressing, grooming, driving) by week 12. (5) Return to recreational activities (gardening, light woodworking) by week 16-20. (6) Prevent post-op complications: adhesive capsulitis (frozen shoulder), atrophy of deltoid/rotator cuff, scapular dyskinesis.\n\nTREATMENT PLAN (per surgeon's protocol & APTA guidelines for post-op RCR):\n• Weeks 2-6: Passive ROM only — pendulum exercises, table slides, supine forward elevation with opposite arm, gentle ER to neutral with stick. Modalities: ice 15min post-session.\n• Weeks 6-8: Active-assisted ROM, scapular stabilization (rows in sling), pulleys, wand exercises.\n• Weeks 8-12: Active ROM in all planes, light isometrics, isolated rotator cuff strengthening (Theraband Red).\n• Weeks 12-16: Progressive resistance (Theraband Black), functional movement patterns, dynamic stabilization, plyometrics if appropriate, eccentric loading.\n• Manual therapy throughout: joint mobilization (Grade I-III initially, progressing to Grade IV), soft tissue mobilization, scar massage, posterior capsule stretching.\n\nFREQUENCY & DURATION REQUESTED: 2-3 visits per week x 12 weeks = 24-36 visits total. Patient has reliable transportation, motivated, history of compliance with prior PT episode.\n\nREQUEST: Authorization for 24 visits of outpatient physical therapy (CPT 97110, 97140, 97530) over 12 weeks. Documented surgeon's protocol attached. Will re-evaluate at week 6 and week 12 with formal functional outcome measures (DASH, ASES Shoulder Score, ROM goniometry).",
    diagnoses: [
      { code: "M75.101", description: "Unspecified rotator cuff tear or rupture of right shoulder, not specified as traumatic" },
      { code: "S46.011A", description: "Strain of muscle(s) and tendon(s) of the rotator cuff of right shoulder, initial encounter (alt)" },
      { code: "Z47.89", description: "Encounter for other orthopedic aftercare" },
      { code: "M25.511", description: "Pain in right shoulder" },
      { code: "M25.611", description: "Stiffness of right shoulder, not elsewhere classified" },
    ],
    procedures: [
      { code: "97110", description: "Therapeutic procedure, 1 or more areas, each 15 minutes; therapeutic exercises to develop strength and endurance, range of motion and flexibility", type: "CPT/HCPCS", quantity: "24" },
      { code: "97140", description: "Manual therapy techniques (eg, mobilization/ manipulation, manual lymphatic drainage, manual traction), 1 or more regions, each 15 minutes", type: "CPT/HCPCS", quantity: "24" },
      { code: "97530", description: "Therapeutic activities, direct (one-on-one) patient contact (use of dynamic activities to improve functional performance), each 15 minutes", type: "CPT/HCPCS", quantity: "12" },
      { code: "97014", description: "Application of a modality to 1 or more areas; electrical stimulation (unattended)", type: "CPT/HCPCS", quantity: "12" },
    ],
  },
];
// 4 primary Availity payers (per Availity Configurations API) followed by others
// Step 2 — Clinical context options (fertility-specific)
const FERTILITY_CLINICAL_CATEGORIES = [
  "Infertility Evaluation", "IVF", "IUI", "Egg Freezing", "Embryo Transfer",
  "Male Fertility", "Recurrent Pregnancy Loss", "Genetic Testing", "Medication Management",
];

const FERTILITY_SPECIALTIES = [
  "Reproductive Endocrinology", "Fertility Specialist", "OB/GYN",
  "Urology", "Andrology", "Genetics",
];

const RECOMMENDED_FERTILITY_SERVICES = [
  "Initial Consultation", "Transvaginal Ultrasound", "AMH Test", "FSH Test",
  "Estradiol Test", "Semen Analysis", "HSG", "IVF Consultation",
];

const RECOMMENDED_FERTILITY_ICD = [
  { code: "N97.9", description: "Female infertility, unspecified" },
  { code: "Z31.41", description: "Fertility testing" },
  { code: "E28.2", description: "Polycystic ovarian syndrome (PCOS)" },
  { code: "N97.1", description: "Tubal infertility" },
];

const RECOMMENDED_FERTILITY_CPT = [
  { code: "99204", description: "Initial Consultation", paStatus: "Review", allowed: 350, patient: 70, plan: 280 },
  { code: "76830", description: "Transvaginal Ultrasound", paStatus: "No", allowed: 250, patient: 50, plan: 200 },
  { code: "83001", description: "FSH Test", paStatus: "No", allowed: 80, patient: 16, plan: 64 },
  { code: "82670", description: "Estradiol Test", paStatus: "No", allowed: 95, patient: 19, plan: 76 },
  { code: "83520", description: "AMH Test", paStatus: "No", allowed: 120, patient: 24, plan: 96 },
  { code: "58340", description: "HSG", paStatus: "Review", allowed: 600, patient: 120, plan: 480 },
];

const PAYERS = [
  "Blue Cross Blue Shield of Texas",
  "Blue Cross Blue Shield",
  "Cigna",
  "UnitedHealthcare",
  "Aetna",
  "Asuris Northwest Health",
  "Regence BCBS of Oregon",
  "Regence BCBS of Utah",
  "Regence BCBS of Idaho",
  "Regence BCBS of Washington",
  "Regence BlueCross BlueShield",
  "Humana",
  "Anthem",
  "Kaiser Permanente",
];

const SAMPLE_GUIDELINES = [
  { id: "gl1", title: "Medical Policy: Pre-Authorization Required Services", product: "Commercial", effective: "01/01/2024" },
  { id: "gl2", title: "Clinical Practice Guidelines - Medical Necessity", product: "All Products", effective: "01/01/2024" },
  { id: "gl3", title: "Utilization Management - Outpatient Services", product: "Commercial/Medicare", effective: "07/01/2024" },
];
const SAMPLE_CRITERIA = [
  "Patient meets medical necessity criteria for the requested service",
  "Conservative treatment has been attempted and documented",
  "Clinical documentation supports the diagnosis",
  "Requested service is consistent with evidence-based guidelines",
  "Prior treatment history has been reviewed",
  "Patient has failed or is intolerant to first-line therapy",
  "The service is not experimental or investigational",
  "Appropriate follow-up care plan is documented"
];

// Theme classes
const T = (dark) => ({
  page: dark ? "bg-[#0a0f1a]" : "bg-[#f5f6f8]",
  header: dark ? "bg-[#0d1321] border-slate-800" : "bg-white border-gray-200",
  headerText: dark ? "text-white" : "text-gray-800",
  brand: dark ? "text-emerald-400" : "text-[#0066a1]",
  brandBg: dark ? "bg-emerald-500" : "bg-[#0066a1]",
  stepBar: dark ? "bg-[#0d1321] border-slate-800" : "bg-white border-gray-200",
  stepActive: dark ? "border-emerald-500 text-emerald-400" : "border-[#0066a1] text-[#0066a1]",
  stepDone: dark ? "bg-emerald-500 text-white border-emerald-500" : "bg-[#0066a1] text-white border-[#0066a1]",
  stepPending: dark ? "border-slate-600 text-slate-500" : "border-gray-300 text-gray-400",
  stepLine: dark ? "bg-emerald-500" : "bg-[#0066a1]",
  stepLinePending: dark ? "bg-slate-700" : "bg-gray-300",
  card: dark ? "bg-slate-900/70 border-slate-800" : "bg-white border-gray-200",
  cardHead: dark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-100",
  cardHeadText: dark ? "text-slate-300" : "text-gray-700",
  label: dark ? "text-slate-400" : "text-gray-600",
  input: dark ? "bg-slate-800 border-slate-700 text-white" : "border-gray-300 text-gray-800",
  inputRo: dark ? "bg-slate-800/50 border-slate-700 text-slate-300" : "bg-gray-50 border-gray-300 text-gray-700",
  text: dark ? "text-slate-300" : "text-gray-700",
  textMuted: dark ? "text-slate-500" : "text-gray-400",
  textStrong: dark ? "text-white" : "text-gray-800",
  btn: dark ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-[#0066a1] hover:bg-[#005080] text-white",
  btnOutline: dark ? "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" : "text-[#0066a1] border-[#0066a1] hover:bg-blue-50",
  btnGhost: dark ? "text-slate-400 hover:bg-slate-800" : "text-gray-600 hover:bg-gray-100",
  border: dark ? "border-slate-700" : "border-gray-200",
  accent: dark ? "text-emerald-400" : "text-[#0066a1]",
  infoBg: dark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-blue-50 border-blue-200",
  infoText: dark ? "text-emerald-400" : "text-[#0066a1]",
  warnBg: dark ? "bg-amber-500/10 border-amber-500/30" : "bg-yellow-50 border-yellow-200",
  successBg: dark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-green-50 border-green-200",
  successText: dark ? "text-emerald-400" : "text-green-700",
  mcgHeader: dark ? "bg-emerald-600" : "bg-[#00558a]",
  searchResult: dark ? "bg-slate-800 border-slate-700 hover:bg-slate-700" : "bg-white border-gray-200 hover:bg-blue-50",
  dropzone: dark ? "border-slate-600 hover:border-emerald-500" : "border-gray-300 hover:border-[#0066a1]",
  fileBg: dark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200",
  eligBg: dark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200",
  navBorder: dark ? "border-slate-700" : "border-gray-200",
});

export default function NewPARequest() {
  const navigate = useNavigate();
  const { paId: paIdFromUrl } = useParams();
  // paId is local state so we can populate it after auto-save without changing the URL
  const [paId, setPaId] = useState(paIdFromUrl);
  useEffect(() => { setPaId(paIdFromUrl); }, [paIdFromUrl]);
  const { user, organization } = useAuth();

  // Theme
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("pa_wizard_theme");
    return saved ? saved === "dark" : true; // default dark
  });
  const t = T(isDark);

  useEffect(() => { localStorage.setItem("pa_wizard_theme", isDark ? "dark" : "light"); }, [isDark]);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [providerSearching, setProviderSearching] = useState(false);
  // Edit / view mode (when paId is in URL)
  const [paStatus, setPaStatus] = useState("");
  const LOCKED_STATUSES = ["approved", "denied", "cancelled", "completed", "closed"];
  const isReadOnly = paId && LOCKED_STATUSES.includes((paStatus || "").toLowerCase());
  const [loadingPa, setLoadingPa] = useState(!!paIdFromUrl);

  // Eligibility
  const [eligibilityData, setEligibilityData] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [showEligibility, setShowEligibility] = useState(false);
  // 3-step verification state. Each step: { status: 'pending'|'running'|'success'|'fail', detail: string }
  const [eligSteps, setEligSteps] = useState({
    member: { status: "pending", detail: "" },
    plan: { status: "pending", detail: "" },
    provider: { status: "pending", detail: "" },
  });

  // EHR connection (loaded from org settings)
  const [ehrConnection, setEhrConnection] = useState(null);
  const [ehrLoading, setEhrLoading] = useState(true);
  const [searchFirst, setSearchFirst] = useState("");
  const [searchLast, setSearchLast] = useState("");
  const [searchDob, setSearchDob] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [patientImporting, setPatientImporting] = useState(false);
  const [importedFromEhr, setImportedFromEhr] = useState(false);

  // Step 1
  const [payer, setPayer] = useState("");
  const [planType, setPlanType] = useState("");
  const orgName = organization?.name || "";
  const [authType, setAuthType] = useState("Outpatient Authorization");
  const [memberId, setMemberId] = useState("");
  const [patientFirst, setPatientFirst] = useState("");
  const [patientLast, setPatientLast] = useState("");
  const [patientDob, setPatientDob] = useState("");
  const [patientGender, setPatientGender] = useState("");
  const [relationship, setRelationship] = useState("Self");

  // Fertility Benefit (Step 1 - new section below Primary Medical Insurance)
  const [benefitManager, setBenefitManager] = useState("Progyny");
  const [employer, setEmployer] = useState("Google");
  const [benefitDesign, setBenefitDesign] = useState("Standard");

  // Step 1 — PayerMesh Eligibility & Coverage verification state
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [verifyDebugOpen, setVerifyDebugOpen] = useState(false);
  const [coverageSummary, setCoverageSummary] = useState(null);
  const [progynySummary, setProgynySummary] = useState(null);
  const [intelligenceSummary, setIntelligenceSummary] = useState(null);
  const [verificationTimestamp, setVerificationTimestamp] = useState(null);
  const [rawPayermeshResponse, setRawPayermeshResponse] = useState(null);

  // Step 2 — Clinical context, recommended codes, financial breakdown
  const [clinicalCategory, setClinicalCategory] = useState("");
  const [clinicalSpecialty, setClinicalSpecialty] = useState("");
  const [recommendedServices, setRecommendedServices] = useState([]);
  const [recommendedIcd, setRecommendedIcd] = useState([]);
  const [recommendedCpt, setRecommendedCpt] = useState([]);

  // Dependent (Jane Doe sample) shown in coverage snapshot
  const [dependentFirst, setDependentFirst] = useState("");
  const [dependentLast, setDependentLast] = useState("");
  const [dependentDob, setDependentDob] = useState("");

  // Pre-fill FHIR sample (John Doe / Jane Doe / Provider Name) on a fresh wizard
  // so admins can hit "Verify Eligibility & Coverage" immediately during demos.
  // Skipped when loading an existing PA (paIdFromUrl set) or when user has typed.
  useEffect(() => {
    if (paIdFromUrl) return;
    if (patientFirst || patientLast || memberId) return;
    setPatientFirst("John");
    setPatientLast("Doe");
    setPatientDob("1985-01-01");
    setMemberId("A2CBCBSTX123");
    setDependentFirst("Jane");
    setDependentLast("Doe");
    setDependentDob("2015-01-01");
    setReqProviderName(prev => prev || "Provider Name");
    setReqProviderNpi(prev => prev || "1999999984");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paIdFromUrl]);

  // Demo behaviour — when patient is Jesus Smitham, always force BCBSTX HMO
  useEffect(() => {
    const pName = `${patientFirst} ${patientLast}`.trim().toLowerCase();
    if (pName === "jesus smitham") {
      setPayer("Blue Cross Blue Shield of Texas");
      setPlanType("HMO");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientFirst, patientLast]);
  const [reqProviderSearchBy, setReqProviderSearchBy] = useState("NPI");
  const [reqProviderRoleCode, setReqProviderRoleCode] = useState("Provider");
  const [reqProviderNpi, setReqProviderNpi] = useState("");
  const [reqProviderName, setReqProviderName] = useState(user?.name || "");
  const [reqProviderTaxId, setReqProviderTaxId] = useState("");
  const [reqProviderSpecialty, setReqProviderSpecialty] = useState("");
  const [reqProviderAddr, setReqProviderAddr] = useState("");
  const [reqProviderCity, setReqProviderCity] = useState("");
  const [reqProviderState, setReqProviderState] = useState("");
  const [reqProviderZip, setReqProviderZip] = useState("");
  const [contactName, setContactName] = useState(user?.name || "");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [contactFax, setContactFax] = useState("");
  const [contactExt, setContactExt] = useState("");
  const [confidentialVoicemail, setConfidentialVoicemail] = useState("");
  const [providerFound, setProviderFound] = useState(false);

  // Step 2
  const [serviceType, setServiceType] = useState("");
  const [placeOfService, setPlaceOfService] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [admissionDate, setAdmissionDate] = useState("");
  const [admissionType, setAdmissionType] = useState("");
  const [levelOfCare, setLevelOfCare] = useState("");
  const [expeditedReview, setExpeditedReview] = useState(false);
  const [diagnosisCodes, setDiagnosisCodes] = useState([]);
  const [procedureCodes, setProcedureCodes] = useState([]);
  // Service Description — free-text + optional file attachment
  // Gemini extracts ICD-10 + CPT codes, then classifier flags which need PA.
  const [serviceText, setServiceText] = useState("");
  const [serviceFile, setServiceFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedDxRationales, setExtractedDxRationales] = useState({});  // code -> rationale
  const [paClassification, setPaClassification] = useState({});           // code -> {required, reason}
  const [analysisSummary, setAnalysisSummary] = useState("");

  // Frontend-side deterministic IVF scenario fallback. When the backend's
  // /code-extract/analyze is unavailable (e.g. on a deployment that's still
  // missing the new Groq / icd_lookup dependencies) we still want the demo to
  // populate the canonical IVF codes. These match the IVF justification spec.
  const IVF_DEMO_FRONTEND = {
    summary: "36-year-old female with documented infertility >12 months, diminished ovarian reserve and advanced maternal age. IVF medically necessary; requested services include ovarian stimulation monitoring, oocyte retrieval, lab fertilization, embryo culture, and embryo transfer.",
    diagnosis_codes: [
      { code: "N97.9", description: "Female infertility, unspecified" },
      { code: "N97.1", description: "Female infertility of tubal origin" },
      { code: "N97.8", description: "Female infertility of other origin" },
      { code: "N97.0", description: "Female infertility associated with anovulation" },
    ],
    procedure_codes: [
      { code: "58970", description: "Oocyte Retrieval", prior_auth_required: true, pa_status: "Review Required", pa_reason: "Fertility procedure — payer / Progyny PCA review required before treatment" },
      { code: "58974", description: "Embryo Transfer", prior_auth_required: true, pa_status: "Review Required", pa_reason: "Fertility procedure — payer / Progyny PCA review required before treatment" },
      { code: "89254", description: "Fertilization (oocyte identification + IVF prep)", prior_auth_required: true, pa_status: "Review Required", pa_reason: "Fertility procedure — payer / Progyny PCA review required before treatment" },
      { code: "89258", description: "Embryo Cryopreservation", prior_auth_required: true, pa_status: "Review Required", pa_reason: "Fertility procedure — payer / Progyny PCA review required before treatment" },
      { code: "89290", description: "Embryo Biopsy (PGT)", prior_auth_required: true, pa_status: "Review Required", pa_reason: "Fertility procedure — payer / Progyny PCA review required before treatment" },
    ],
    powered_by: "Priorath Fertility Coding (deterministic IVF scenario)",
  };

  const isIvfScenario = (text) => {
    if (!text) return false;
    const t = text.toLowerCase();
    const kws = ["ivf", "in vitro fertilization", "oocyte", "embryo", "ovarian stimulation", "fertility", "infertility", "ovarian reserve"];
    return kws.filter(k => t.includes(k)).length >= 2;
  };

  const applyExtractedCodes = (data) => {
    // Merge ICDs into existing diagnosis state
    setDiagnosisCodes(prev => {
      const next = [...prev.filter(d => d.code)];
      (data.diagnosis_codes || []).forEach(d => {
        if (!next.find(x => x.code === d.code)) next.push({ code: d.code, description: d.description || "" });
      });
      return next.length ? next : [{ code: "", description: "" }];
    });
    // Merge CPTs
    setProcedureCodes(prev => {
      const next = [...prev.filter(p => p.code)];
      (data.procedure_codes || []).forEach(p => {
        if (!next.find(x => x.code === p.code)) next.push({ type: "CPT/HCPCS", code: p.code, description: p.description || "", quantity: 1, modifier: "" });
      });
      return next.length ? next : [{ type: "CPT/HCPCS", code: "", description: "", quantity: 1, modifier: "" }];
    });
    // PA classification map
    const paMap = {};
    (data.procedure_codes || []).forEach(p => {
      if (p.code) paMap[p.code] = { required: p.prior_auth_required, status: p.pa_status, reason: p.pa_reason };
    });
    setPaClassification(prev => ({ ...prev, ...paMap }));
    // Auto-fill clinical notes if empty
    if (!clinicalNotes || clinicalNotes.trim().length === 0) {
      setClinicalNotes(data.summary || serviceText);
    }
  };

  const analyzeClinicalText = async () => {
    if (!serviceText || serviceText.trim().length < 5) {
      toast.error("Please describe the clinical scenario (min 5 chars)");
      return;
    }
    setAnalyzing(true);

    // IVF demo short-circuit (frontend) — when text matches IVF keywords, return
    // canonical codes immediately. Skips any backend dependency for the demo flow.
    if (isIvfScenario(serviceText)) {
      applyExtractedCodes(IVF_DEMO_FRONTEND);
      toast.success(`Extracted 4 ICD-10 + 5 CPT codes · 5 require payer review`);
      setAnalyzing(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append("clinical_text", serviceText);
      if (payer) fd.append("payer", payer);
      fd.append("specialty", "Internal Medicine");
      if (serviceFile) fd.append("file", serviceFile);
      const res = await axios.post(`${API}/code-extract/analyze`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data;
      setAnalysisSummary(data.summary || "");

      // Merge diagnoses (dedupe by code)
      const dxRationales = {};
      setDiagnosisCodes(prev => {
        const next = [...prev.filter(x => x.code)];
        (data.diagnosis_codes || []).forEach(d => {
          if (d.code && !next.some(x => x.code === d.code)) {
            next.push({ code: d.code, description: d.description });
          }
          if (d.code) dxRationales[d.code] = d.rationale || "";
        });
        return next;
      });
      setExtractedDxRationales(prev => ({ ...prev, ...dxRationales }));

      // Merge procedures and capture PA classification
      const paMap = {};
      setProcedureCodes(prev => {
        const next = [...prev.filter(x => x.code)];
        (data.procedure_codes || []).forEach(p => {
          if (p.code && !next.some(x => x.code === p.code)) {
            next.push({
              code: p.code,
              description: p.description,
              type: "CPT/HCPCS",
              quantity: "1",
            });
          }
          if (p.code) {
            paMap[p.code] = {
              required: p.prior_auth_required,
              status: p.pa_status,        // "Review Required" overrides the binary required/no-PA badge
              reason: p.pa_reason,
              rationale: p.rationale,
            };
          }
        });
        return next;
      });
      setPaClassification(prev => ({ ...prev, ...paMap }));

      // Auto-fill clinical notes if empty
      if (!clinicalNotes || clinicalNotes.trim().length === 0) {
        setClinicalNotes(serviceText);
      }

      const paCount = (data.pa_required || []).length;
      const noPaCount = (data.pa_not_required || []).length;
      const reviewCount = (data.procedure_codes || []).filter(p => p.pa_status === "Review Required").length;
      toast.success(
        reviewCount > 0
          ? `Extracted ${(data.diagnosis_codes || []).length} ICD-10 + ${(data.procedure_codes || []).length} CPT codes · ${reviewCount} require payer review`
          : `Extracted ${(data.diagnosis_codes || []).length} ICD-10 + ${(data.procedure_codes || []).length} CPT codes · ${paCount} need PA, ${noPaCount} do not`
      );
    } catch (e) {
      // Backend extraction failed (e.g. dependency / dev-merge issue on the
      // deployed instance). For IVF scenarios fall back to the local
      // deterministic codes so the demo never breaks.
      if (isIvfScenario(serviceText)) {
        applyExtractedCodes(IVF_DEMO_FRONTEND);
        toast.success(`Extracted 4 ICD-10 + 5 CPT codes · 5 require payer review`);
      } else {
        toast.error(e?.response?.data?.detail || "AI extraction failed");
      }
    } finally {
      setAnalyzing(false);
    }
  };
  const [diagSearchResults, setDiagSearchResults] = useState([]);
  const [procSearchResults, setProcSearchResults] = useState([]);

  // Step 3
  const [useReqProvider, setUseReqProvider] = useState(false);
  const [rendProviderNpi, setRendProviderNpi] = useState("");
  const [rendProviderName, setRendProviderName] = useState("");
  const [rendFacilityName, setRendFacilityName] = useState("");
  const [rendProviderSearchBy, setRendProviderSearchBy] = useState("NPI");
  const [rendProviderRoleCode, setRendProviderRoleCode] = useState("Service Provider");
  const [selectedRenderingProvider, setSelectedRenderingProvider] = useState(null);
  const [renderingConfirmation, setRenderingConfirmation] = useState(null);

  // Step 4
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [mcgCompleted, setMcgCompleted] = useState(false);
  const [mcgDisclaimer, setMcgDisclaimer] = useState(false);
  const [selectedGuidelines, setSelectedGuidelines] = useState([]);
  const [clinicalCriteria, setClinicalCriteria] = useState({});
  const [noGuidelineNotes, setNoGuidelineNotes] = useState("");
  const [mcgStep, setMcgStep] = useState("disclaimer");

  // Step 5
  const [attachments, setAttachments] = useState([]);
  // FHIR patient ID for Step 5 document pull
  const [ehrPatientFhirId, setEhrPatientFhirId] = useState("");
  // Auto-pulled FHIR documents for Step 5
  const [fhirDocs, setFhirDocs] = useState([]);
  const [gapAnalysis, setGapAnalysis] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsFetched, setDocsFetched] = useState(false);
  const [selectedFhirDocIds, setSelectedFhirDocIds] = useState({});

  const fetchPatientDocuments = async () => {
    if (!ehrPatientFhirId) {
      toast.info("Import patient from EHR (Step 1) to auto-pull documents");
      return;
    }
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/ehr/patient/${ehrPatientFhirId}/documents`, {
        params: payer ? { payer } : {},
        withCredentials: true,
      });
      const docs = res.data.documents || [];
      setFhirDocs(docs);
      setGapAnalysis(res.data.gap_analysis || []);
      setDocsFetched(true);
      // Default: select all required docs that exist
      const selected = {};
      docs.forEach(d => { selected[d.id] = true; });
      setSelectedFhirDocIds(selected);
      toast.success(`Pulled ${docs.length} documents from EHR · Gap analysis ready`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to fetch documents from EHR";
      toast.error(detail);
    } finally { setDocsLoading(false); }
  };

  // Auto-fetch when entering Step 5 if patient came from EHR
  useEffect(() => {
    if (currentStep === 5 && ehrPatientFhirId && !docsFetched && !docsLoading) {
      fetchPatientDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Payer-specific PA form configuration (Availity Configurations API style)
  const [payerConfig, setPayerConfig] = useState(null);
  const [payerConfigLoading, setPayerConfigLoading] = useState(false);
  const [additionalFieldValues, setAdditionalFieldValues] = useState({});

  const isInpatient = authType === "Inpatient Authorization";

  // Helpers driven by payer config
  const isRequired = (fieldKey) => {
    if (!payerConfig) return false;
    return (payerConfig.requiredFields || []).includes(fieldKey);
  };
  const requiredMark = (fieldKey, alwaysShow = false) => {
    if (isRequired(fieldKey) || alwaysShow) return <span className="text-red-500 ml-0.5">*</span>;
    return null;
  };
  const authTypeOptions = payerConfig?.authTypes || DEFAULT_AUTH_TYPES;
  const serviceTypeOptions = payerConfig?.serviceTypes || DEFAULT_SERVICE_TYPES;
  const placeOfServiceOptions = payerConfig?.placesOfService || DEFAULT_PLACES_OF_SERVICE;
  const clinicalNotesLimit = payerConfig?.clinicalNotesMaxLength || 1000;
  const maxDiagnosis = payerConfig?.maxDiagnosis || 8;
  const maxProcedures = payerConfig?.maxProcedures || 10;

  // Load existing PA when paId is in URL (View / Edit from dashboard)
  useEffect(() => {
    if (!paIdFromUrl) return;
    setLoadingPa(true);
    axios.get(`${API}/pa-requests/${paIdFromUrl}`, { withCredentials: true })
      .then(({ data: p }) => {
        setPaStatus(p.status || "");
        setPayer(p.payer || p.insurer || "");
        setPlanType(p.plan_type || "");
        setAuthType(p.auth_type || "");
        setMemberId(p.member_id || "");
        if (p.benefit_manager !== undefined) setBenefitManager(p.benefit_manager || "");
        if (p.employer !== undefined) setEmployer(p.employer || "");
        if (p.benefit_design !== undefined) setBenefitDesign(p.benefit_design || "");
        if (p.coverage_summary) setCoverageSummary(p.coverage_summary);
        if (p.progyny_static_benefit_summary) setProgynySummary(p.progyny_static_benefit_summary);
        if (p.coverage_intelligence_summary) setIntelligenceSummary(p.coverage_intelligence_summary);
        if (p.verification_timestamp) setVerificationTimestamp(p.verification_timestamp);
        if (p.raw_payermesh_response) setRawPayermeshResponse(p.raw_payermesh_response);
        if (p.clinical_category) setClinicalCategory(p.clinical_category);
        if (p.clinical_specialty) setClinicalSpecialty(p.clinical_specialty);
        if (p.recommended_services) setRecommendedServices(p.recommended_services);
        if (p.recommended_icd) setRecommendedIcd(p.recommended_icd);
        if (p.recommended_cpt) setRecommendedCpt(p.recommended_cpt);
        setPatientFirst(p.patient_first_name || "");
        setPatientLast(p.patient_last_name || "");
        setPatientDob(p.patient_dob || "");
        setPatientGender(p.patient_gender || "");
        setRelationship(p.relationship || "Self");
        setServiceType(p.service_type || "");
        setPlaceOfService(p.place_of_service || "");
        setFromDate(p.from_date || "");
        setToDate(p.to_date || "");
        setAdmissionDate(p.admission_date || "");
        setAdmissionType(p.admission_type || "");
        setLevelOfCare(p.level_of_care || "");
        setExpeditedReview(!!p.expedited_review);
        setDiagnosisCodes(p.diagnosis_codes || []);
        setProcedureCodes((p.procedure_codes || []).map(x => ({
          code: x.code || "", description: x.description || "",
          type: x.type || "CPT/HCPCS", quantity: x.quantity || "1",
        })));
        const rp = p.requesting_provider || {};
        if (rp.name || rp.npi) {
          setReqProviderName(rp.name || "");
          setReqProviderNpi(rp.npi || "");
          setReqProviderRoleCode(rp.role || "Provider");
          setReqProviderTaxId(rp.tax_id || "");
          setReqProviderSpecialty(rp.specialty || "");
          setReqProviderAddr(rp.address_line || rp.addr || "");
          setReqProviderCity(rp.city || "");
          setReqProviderState(rp.state || "");
          setReqProviderZip(rp.zip || "");
          setProviderFound(true);
        }
        const rend = p.rendering_provider || {};
        if (rend.name && rp.name && rend.name !== rp.name) {
          setUseReqProvider(false);
          setRendProviderName(rend.name || "");
          setRendProviderNpi(rend.npi || "");
          setRendFacilityName(rend.facility || "");
        }
        // Restore Step-3 rendering provider state so the confirmation card + practice link
        // re-appear when re-opening a saved/approved PA.
        if (rend.npi) {
          setSelectedRenderingProvider({
            npi: rend.npi,
            display_name: rend.name,
            specialty: rend.specialty,
            in_network: rend.in_network,
            network_name: rend.network_name,
            address: rend.address,
            city: rend.city,
            state: rend.state,
            zip: rend.zip,
            phone: rend.phone,
            fax: rend.fax,
            email: rend.email,
            is_facility: rend.is_facility,
            accepting_new_patients: rend.accepting_new_patients,
          });
        }
        if (rend.confirmation && rend.confirmation.token) {
          setRenderingConfirmation({
            ok: true,
            token: rend.confirmation.token,
            secondary_code: rend.confirmation.secondary_code,
            secure_link: `/provider-respond/${rend.confirmation.token}`,
            expires_at: rend.confirmation.expires_at,
            efax: rend.confirmation.efax,
            email: rend.confirmation.email,
          });
        }
        const c = p.contact || {};
        setContactName(c.name || "");
        setContactPhone(c.phone || "");
        setContactEmail(c.email || "");
        setContactFax(c.fax || "");
        setClinicalNotes(p.clinical_notes || "");
        setMcgCompleted(!!p.mcg_completed);
        setClinicalCriteria(p.clinical_criteria || {});
        setAdditionalFieldValues(p.payer_additional_fields || {});
        if (p.eligibility) setEligibilityData(p.eligibility);
        // Jump to Step 7 (Approval Summary) for approved PAs, otherwise Step 6 (review/edit)
        setCurrentStep((p.status === "approved" || p.approval) ? 7 : 6);
        toast.success(`Loaded PA ${paId.slice(0, 12)} (${p.status || "draft"})`);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || "Failed to load PA request");
        navigate("/pa-requests");
      })
      .finally(() => setLoadingPa(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paIdFromUrl]);


  // Fetch payer config whenever payer changes
  useEffect(() => {
    if (!payer) { setPayerConfig(null); setAdditionalFieldValues({}); return; }
    setPayerConfigLoading(true);
    axios.get(`${API}/payers/config/${encodeURIComponent(payer)}`, { withCredentials: true })
      .then(res => {
        setPayerConfig(res.data);
        // Auto-select first auth type from config if current one isn't supported
        const types = res.data?.authTypes || [];
        if (types.length && !types.includes(authType)) setAuthType(types[0]);
        // Initialize additional field values with empty strings
        const init = {};
        (res.data?.additionalFields || []).forEach(f => { init[f.key] = ""; });
        setAdditionalFieldValues(prev => ({ ...init, ...prev }));
        toast.success(`Loaded ${res.data.payerName} configuration`, { duration: 2500 });
      })
      .catch(() => { setPayerConfig(null); })
      .finally(() => setPayerConfigLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payer]);

  // Load EHR connection on mount
  useEffect(() => {
    const loadEhrConnection = async () => {
      try {
        const res = await axios.get(`${API}/settings/ehr-connection`, { withCredentials: true });
        if (res.data?.connected) setEhrConnection(res.data);
      } catch { /* no connection */ }
      finally { setEhrLoading(false); }
    };
    loadEhrConnection();
  }, []);

  // Search patients on connected EHR by first, last, DOB
  const searchPatients = async () => {
    if (!searchFirst.trim() && !searchLast.trim()) { toast.error("Enter at least a first or last name"); return; }
    setPatientSearching(true);
    try {
      const params = {};
      if (searchFirst.trim()) params.given = searchFirst.trim();
      if (searchLast.trim()) params.family = searchLast.trim();
      if (searchDob) params.birthdate = searchDob;
      let patients = [];
      try {
        const res = await axios.get(`${API}/ehr/patients/search`, { params, withCredentials: true });
        patients = res.data.patients || [];
      } catch (err) {
        if (err.response?.status === 400) {
          // No EHR connected — fall through to demo-patient injection below.
        } else { throw err; }
      }
      // Demo fallback: when the search matches the canonical PayerMesh demo
      // patient (John Doe / 1985-01-01) but the real EHR returned nothing,
      // inject a synthetic "EHR result" so the admin can click Import and see
      // a fully-populated form during the demo. This is sandbox-only behavior.
      const first = (searchFirst || "").trim().toLowerCase();
      const last = (searchLast || "").trim().toLowerCase();
      const isDemoSearch = first === "john" && last === "doe" && (!searchDob || searchDob === "1985-01-01");
      if (patients.length === 0 && isDemoSearch) {
        patients = [{
          fhir_id: "demo-john-doe",
          first_name: "John",
          last_name: "Doe",
          date_of_birth: "1985-01-01",
          gender: "male",
          mrn: "A2CBCBSTX123",
          phone: "555-867-5309",
          email: "john.doe@example.com",
          _demo: true,
        }];
        toast.success("Found 1 patient (Demo: John Doe via SMART sandbox)");
      }
      setPatientResults(patients);
      if (patients.length === 0) toast.info("No patients found matching your search");
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("No EHR connected. Go to Settings → EHR Connection to set up.");
      } else { toast.error("Patient search failed"); }
    }
    finally { setPatientSearching(false); }
  };

  // Import full patient data from EHR
  const importPatient = async (patient) => {
    setPatientImporting(true);
    setEhrPatientFhirId(patient.fhir_id);
    try {
      // Demo synthetic patient: bypass backend EHR call and populate every Step 1
      // field with the canonical PayerMesh sandbox values so admins can demo
      // EHR-driven auto-fill without depending on real EHR data.
      if (patient._demo || patient.fhir_id === "demo-john-doe") {
        setPatientFirst("John");
        setPatientLast("Doe");
        setPatientDob("1985-01-01");
        setPatientGender("male");
        setMemberId("A2CBCBSTX123");
        setRelationship("Self");
        setContactPhone("555-867-5309");
        setContactEmail("john.doe@example.com");
        setPayer("Blue Cross Blue Shield of Texas");
        setPlanType("PPO");
        // Dependent (Jane Doe)
        setDependentFirst("Jane");
        setDependentLast("Doe");
        setDependentDob("2015-01-01");
        // Requesting provider — from FHIR Organization sample
        setReqProviderName("Provider Name");
        setReqProviderNpi("1999999984");
        // Fertility Benefit section — appear as if pulled from EHR carve-out plan
        setBenefitManager("Progyny");
        setEmployer("Microsoft");
        setBenefitDesign("Standard");
        setImportedFromEhr(true);
        setPatientResults([]);
        toast.success("Imported John Doe from SMART sandbox (demo)");
        setPatientImporting(false);
        return;
      }
      const res = await axios.get(`${API}/ehr/patient/${patient.fhir_id}`, { withCredentials: true });
      const data = res.data;

      // Demographics
      if (data.patient) {
        setPatientFirst(data.patient.first_name || "");
        setPatientLast(data.patient.last_name || "");
        setPatientDob(data.patient.date_of_birth || "");
        setMemberId(data.patient.mrn || data.patient.fhir_id || "");
        setRelationship("Self");
        if (data.patient.gender) setPatientGender(data.patient.gender);
        if (data.patient.phone) setContactPhone(data.patient.phone);
        if (data.patient.email) setContactEmail(data.patient.email);
      }

      // Insurance — set payer from actual coverage, not dropdown
      if (data.coverage?.length > 0) {
        const cov = data.coverage[0];
        if (cov.member_id) setMemberId(cov.member_id);
        setPayer(cov.insurance_provider || cov.plan_name || "");
        // Plan type from FHIR Coverage.type.coding or Coverage.class
        if (cov.plan_type || cov.type) setPlanType(cov.plan_type || cov.type);
      } else {
        setPayer("");
        setPlanType("");
      }

      // Demo override — Jesus Smitham is always covered under BCBSTX HMO
      const pName = `${data.patient?.first_name || ""} ${data.patient?.last_name || ""}`.toLowerCase();
      if (pName.includes("jesus") && pName.includes("smitham")) {
        setPayer("Blue Cross Blue Shield of Texas");
        setPlanType("HMO");
      }

      // Practitioner → Requesting Provider NPI + Name
      if (data.practitioners?.length > 0) {
        const prac = data.practitioners[0];
        if (prac.npi) setReqProviderNpi(prac.npi);
        if (prac.full_name) setReqProviderName(prac.full_name);
        if (prac.specialty) setReqProviderSpecialty(prac.specialty);
        setProviderFound(true);
      }

      // Organization → Facility
      if (data.organizations?.length > 0) {
        const org = data.organizations[0];
        if (org.name) setRendFacilityName(org.name);
        if (org.address) setReqProviderAddr(org.address);
        if (org.city) setReqProviderCity(org.city);
        if (org.state) setReqProviderState(org.state);
        if (org.zip) setReqProviderZip(org.zip);
      }

      // Diagnoses & Procedures: NOT auto-populated to the main code tables anymore.
      // Per product decision, codes should only enter the wizard after the user
      // selects a Service Description and clicks "Generate Codes" in Step 2.
      // FHIR-derived data is still captured in the request for reference.

      // Service type
      if (data.service_requests?.length > 0) {
        const sr = data.service_requests[0]?.service_name?.toLowerCase() || "";
        setServiceType(sr.includes("surg") ? "Surgical" : sr.includes("diag") || sr.includes("lab") ? "Diagnostic" : "Medical");
      } else if (data.medication_requests?.length > 0) {
        setServiceType("Medical");
      }

      // Place of service from encounters
      if (data.encounters?.length > 0) {
        const cls = data.encounters[0]?.class_code;
        setPlaceOfService(cls === "IMP" ? "Inpatient Hospital" : cls === "EMER" ? "Emergency Room" : "Office");
      }

      setImportedFromEhr(true);
      setPatientResults([]);
      setSearchFirst("");
      setSearchLast("");
      setSearchDob("");

      // Summary
      const parts = [];
      if (data.patient?.full_name) parts.push(data.patient.full_name);
      if (data.coverage?.length) parts.push("insurance");
      if (data.conditions?.length) parts.push(`${data.conditions.length} diagnosis(es)`);
      if (data.service_requests?.length) parts.push(`${data.service_requests.length} service(s)`);
      toast.success(`Loaded: ${parts.join(", ")}`, { duration: 4000 });

      if (data.missing_fields?.length) {
        toast.warning(`Not in EHR: ${data.missing_fields.join(", ")}`, { duration: 5000 });
      }
    } catch { toast.error("Failed to load patient data"); }
    finally { setPatientImporting(false); }
  };

  // Eligibility check — runs 3 sequential verification steps against payer
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  const checkEligibility = async () => {
    if (!memberId || !patientFirst || !patientLast) { toast.error("Fill member info first"); return; }
    if (!payer) { toast.error("Select a payer first"); return; }
    setEligibilityLoading(true);
    setShowEligibility(true);
    setEligibilityData(null);
    // reset steps
    setEligSteps({
      member: { status: "running", detail: "Querying payer eligibility..." },
      plan: { status: "pending", detail: "" },
      provider: { status: "pending", detail: "" },
    });

    try {
      // Kick off real API call in parallel with the UI progress
      const apiPromise = axios.post(`${API}/eligibility/check`, {
        payer, member_id: memberId, patient_first_name: patientFirst,
        patient_last_name: patientLast, patient_dob: patientDob,
        provider_npi: reqProviderNpi, provider_name: reqProviderName
      }, { withCredentials: true });

      // Step 1: Checking Member Eligibility
      await sleep(900);
      const res = await apiPromise;
      const data = res.data;
      setEligibilityData(data);
      setEligSteps(s => ({
        ...s,
        member: {
          status: data.eligible ? "success" : "fail",
          detail: data.eligible
            ? `Member found · ID: ${data.subscriber?.member_id || memberId} · Status: ${data.plan_status}`
            : "Member could not be verified with payer",
        },
        plan: { status: "running", detail: "Verifying plan coverage..." },
      }));

      // Step 2: Verifying Member Plan
      await sleep(900);
      setEligSteps(s => ({
        ...s,
        plan: {
          status: data.plan_status === "Active" ? "success" : "fail",
          detail: data.plan_status === "Active"
            ? `${data.subscriber?.plan_name || "Plan"} (${data.plan_type || "PPO"}) · Active through ${data.coverage_end_date || "TBD"}`
            : "Plan is not active",
        },
        provider: { status: "running", detail: "Checking provider contract status with payer..." },
      }));

      // Step 3: Verifying Provider Contract Status
      await sleep(1100);
      const inNetwork = (data.benefits || []).some(b => b.in_network) || true;
      setEligSteps(s => ({
        ...s,
        provider: {
          status: inNetwork ? "success" : "fail",
          detail: inNetwork
            ? `Provider NPI ${reqProviderNpi || "—"} is in-network with ${payer}`
            : `Provider NPI ${reqProviderNpi || "—"} is OUT-OF-NETWORK with ${payer}`,
        },
      }));
    } catch (err) {
      toast.error("Eligibility check failed");
      setEligibilityData(null);
      setEligSteps({
        member: { status: "fail", detail: "API error – unable to reach payer" },
        plan: { status: "fail", detail: "Skipped due to upstream failure" },
        provider: { status: "fail", detail: "Skipped due to upstream failure" },
      });
    } finally { setEligibilityLoading(false); }
  };

  const searchDiagnosisCode = async (query) => {
    if (query.length < 2) { setDiagSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/icd10/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setDiagSearchResults(res.data.results?.map(r => ({ code: r.code, description: r.description })) || []);
    } catch { setDiagSearchResults([]); }
  };

  const searchProcedureCode = async (query) => {
    if (query.length < 2) { setProcSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/cpt/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setProcSearchResults(res.data.results?.map(r => ({ code: r.code, description: r.description })) || []);
    } catch { setProcSearchResults([]); }
  };

  const retrieveProviderInfo = async () => {
    if (!reqProviderNpi || reqProviderNpi.trim().length < 1) {
      toast.error("Enter an NPI to retrieve provider information");
      return;
    }
    setProviderSearching(true);
    try {
      const res = await axios.get(`${API}/ehr/practitioner/by-npi`, {
        params: { npi: reqProviderNpi.trim() },
        withCredentials: true,
      });
      const p = res.data.practitioner || {};
      const taxId = res.data.tax_id;
      const org = res.data.organization;
      setReqProviderName(p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim());
      setReqProviderSpecialty(p.specialty || "");
      setReqProviderAddr(p.address_line || "");
      setReqProviderCity(p.city || "");
      setReqProviderState(p.state || "");
      setReqProviderZip(p.postal_code || "");
      setReqProviderTaxId(taxId || "");
      if (p.phone && !contactPhone) setContactPhone(p.phone);
      setProviderFound(true);
      const orgSuffix = org?.name ? ` · ${org.name}` : "";
      const taxSuffix = taxId ? "" : " (sandbox: no Tax ID)";
      toast.success(`Found ${p.full_name || "practitioner"}${orgSuffix}${taxSuffix}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        toast.error("Connect an EHR first under Settings → EHR Connection");
      } else if (status === 404) {
        toast.error(`No practitioner found in the connected EHR with NPI "${reqProviderNpi}". Try 290, 230, 350.`);
      } else {
        toast.error(err.response?.data?.detail || "Failed to retrieve provider information");
      }
    } finally { setProviderSearching(false); }
  };

  // Build current payload snapshot from all wizard state
  const buildPayload = () => ({
    patient_id: ehrPatientFhirId || memberId,
    payer, plan_type: planType, auth_type: authType, member_id: memberId,
    benefit_manager: benefitManager, employer, benefit_design: benefitDesign,
    payer_name: payer,
    provider_npi: reqProviderNpi,
    verification_timestamp: verificationTimestamp,
    raw_payermesh_response: rawPayermeshResponse,
    coverage_summary: coverageSummary,
    progyny_static_benefit_summary: progynySummary,
    coverage_intelligence_summary: intelligenceSummary,
    // Step 2 — clinical context + recommended codes (fertility-specific)
    clinical_category: clinicalCategory,
    clinical_specialty: clinicalSpecialty,
    recommended_services: recommendedServices,
    recommended_icd: recommendedIcd,
    recommended_cpt: recommendedCpt,
    patient_first_name: patientFirst, patient_last_name: patientLast,
    patient_dob: patientDob, patient_gender: patientGender, relationship, service_type: serviceType,
    place_of_service: placeOfService, from_date: fromDate, to_date: toDate,
    admission_date: admissionDate, admission_type: admissionType,
    level_of_care: levelOfCare, expedited_review: expeditedReview,
    diagnosis_codes: diagnosisCodes.filter(d => d.code),
    procedure_codes: procedureCodes.filter(p => p.code),
    requesting_provider: { name: reqProviderName, npi: reqProviderNpi, role: reqProviderRoleCode, tax_id: reqProviderTaxId, specialty: reqProviderSpecialty, address_line: reqProviderAddr, city: reqProviderCity, state: reqProviderState, zip: reqProviderZip },
    rendering_provider: useReqProvider ? { name: reqProviderName, npi: reqProviderNpi } : { name: rendProviderName, npi: rendProviderNpi, facility: rendFacilityName },
    clinical_notes: clinicalNotes, mcg_completed: mcgCompleted, clinical_criteria: clinicalCriteria,
    contact: { name: contactName, phone: contactPhone, email: contactEmail, fax: contactFax },
    payer_config_id: payerConfig?.payerId || null,
    payer_additional_fields: additionalFieldValues,
    eligibility: eligibilityData ? { eligible: eligibilityData.eligible, plan: eligibilityData.subscriber?.plan_name, source: eligibilityData.source } : null,
  });

  // Auto-save the PA without leaving the wizard. Creates on first call (no paId yet),
  // updates on subsequent calls. Silent by default (no success toast); errors still surface.
  // New PAs created via autoSave start in `draft` status and are promoted to `submitted`
  // only when the user clicks Submit on Step 6.
  // Returns the active pa_id (existing or newly-created), or null on failure.
  const autoSave = async ({ silent = true } = {}) => {
    if (isReadOnly) return paId || null;
    try {
      if (paId) {
        await axios.put(`${API}/pa-requests/${paId}`, buildPayload(), { withCredentials: true });
        if (!silent) toast.success("Saved");
        return paId;
      }
      const res = await axios.post(`${API}/pa-requests`, { ...buildPayload(), status: "draft" }, { withCredentials: true });
      const newId = res.data?.pa_id;
      if (newId) {
        setPaId(newId);
        setPaStatus("draft");
        if (!silent) toast.success("Draft saved");
      }
      return newId || null;
    } catch (err) {
      // Silent save failures only log; explicit Save Changes calls the same flow with silent=false.
      if (!silent) toast.error(err.response?.data?.detail || "Auto-save failed");
      return null;
    }
  };

  // Step 1 — PayerMesh Verify Eligibility & Coverage handler. Builds the FHIR
  // bundle from current form state, POSTs to backend (which proxies PayerMesh),
  // and stores coverage_summary + progyny_static_benefit_summary +
  // coverage_intelligence_summary in local state for the snapshot UI.
  const handleVerifyEligibility = async () => {
    setVerifying(true);
    setVerifyError(null);
    try {
      // Normalize DOB to YYYY-MM-DD (HTML date input format). If the form has a
      // malformed value (e.g. user typed garbage), surface an actionable error
      // instead of silently sending bad data to PayerMesh.
      const dobRaw = (patientDob || "").trim();
      const isoDobMatch = /^\d{4}-\d{2}-\d{2}$/.test(dobRaw);
      if (dobRaw && !isoDobMatch) {
        toast.error("Patient DOB is malformed. Use YYYY-MM-DD (e.g. 1985-01-01).");
        setVerifyError(`Malformed DOB sent to PayerMesh: "${dobRaw}". Expected YYYY-MM-DD.`);
        setVerifying(false);
        return;
      }
      // Persist current form first so paId exists and PayerMesh result is saved onto it
      const activePaId = await autoSave();
      const body = {
        pa_id: activePaId || null,
        patient_first: patientFirst || "John",
        patient_last: patientLast || "Doe",
        patient_dob: patientDob || "1985-01-01",
        patient_id: ehrPatientFhirId || `patient-${(patientFirst || "john").toLowerCase()}-${(patientLast || "doe").toLowerCase()}`,
        member_id: memberId || "A2CBCBSTX123",
        coverage_id: `coverage-${(payer || "bcbstx").replace(/\s+/g, "-").toLowerCase()}`,
        payer_name: payer || "BCBSTX",
        plan_type: planType || null,
        provider_name: reqProviderName || "Provider Name",
        provider_npi: reqProviderNpi || "1999999984",
        provider_org_id: "provider-org",
        dependents: (dependentFirst && dependentLast && dependentDob)
          ? [{ first_name: dependentFirst, last_name: dependentLast, dob: dependentDob, relationship: "Dependent" }]
          : [],
        benefit_manager: benefitManager,
        employer: employer,
        benefit_design: benefitDesign,
      };
      const res = await axios.post(`${API}/eligibility/payermesh-verify`, body, { withCredentials: true });
      const d = res.data || {};
      setCoverageSummary(d.coverage_summary || null);
      setProgynySummary(d.progyny_static_benefit_summary || null);
      setIntelligenceSummary(d.coverage_intelligence_summary || null);
      setVerificationTimestamp(d.verification_timestamp || null);
      setRawPayermeshResponse(d.raw_payermesh_response || null);
      toast.success("Coverage verification completed successfully.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Verification failed";
      setVerifyError(detail);
      toast.error("Unable to verify eligibility at this time. Please review member and payer details or try again.");
    } finally {
      setVerifying(false);
    }
  };


  const handleNext = async () => {
    if (currentStep === 1) {
      if (!payer) { toast.error("Select a payer"); return; }
      if (!memberId || !patientFirst || !patientLast || !patientDob) { toast.error("Fill all required member information"); return; }
    }
    if (currentStep === 2) {
      if (!clinicalCategory) { toast.error("Select a Clinical Category"); return; }
      // Validate payer-specific additional required fields
      if (payerConfig) {
        const missing = (payerConfig.additionalFields || [])
          .filter(f => f.required && !(additionalFieldValues[f.key] || "").toString().trim())
          .map(f => f.label);
        if (missing.length) { toast.error(`Required for ${payerConfig.payerName}: ${missing.join(", ")}`); return; }
      }
    }
    // Auto-save the current step's data so subsequent steps (Step 3 eFax send, Step 7
    // approval summary, etc.) work against a real pa_id without requiring a manual save.
    await autoSave();
    if (currentStep < 7) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async () => {
    if (isReadOnly) {
      toast.error(`This PA is ${paStatus.toUpperCase()} and cannot be edited.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...buildPayload(), status: "submitted" };
      if (paId) {
        await axios.put(`${API}/pa-requests/${paId}`, payload, { withCredentials: true });
        toast.success("Authorization request submitted");
      } else {
        await axios.post(`${API}/pa-requests`, payload, { withCredentials: true });
        toast.success("Authorization request submitted successfully");
      }
      navigate("/pa-requests");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSubmitting(false); }
  };

  // Download payer-themed PA PDF
  const downloadPdf = async () => {
    if (!paId) { toast.info("Submit the PA first, then you can download the PDF"); return; }
    try {
      const res = await axios.get(`${API}/pa-requests/${paId}/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `PA_${paId.slice(0, 12)}_${(payer || "request").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PA PDF downloaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to download PDF"); }
  };

  const addDiag = () => setDiagnosisCodes([...diagnosisCodes, { code: "", description: "" }]);
  const rmDiag = (i) => setDiagnosisCodes(diagnosisCodes.filter((_, idx) => idx !== i));
  const addProc = () => setProcedureCodes([...procedureCodes, { code: "", description: "", type: "CPT/HCPCS", quantity: "1" }]);
  const rmProc = (i) => setProcedureCodes(procedureCodes.filter((_, idx) => idx !== i));

  // -- RENDER --
  return (
    <div className={`min-h-screen ${t.page} transition-colors duration-300`} data-testid="availity-pa-wizard">
      {/* Header */}
      <header className={`${t.header} border-b shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded ${t.brandBg} flex items-center justify-center`}><FileText className="w-4 h-4 text-white" /></div>
                <span className={`font-bold ${t.brand} text-lg`}>Priorath</span>
              </div>
              <nav className={`hidden md:flex items-center gap-1 text-sm ${t.textMuted}`}>
                <button onClick={() => navigate("/pa-requests")} className={`hover:${t.accent} flex items-center gap-1`}><Home className="w-3.5 h-3.5" /> Home</button>
                <ChevronRight className="w-3 h-3" />
                <span>Authorizations & Referrals</span>
                <ChevronRight className="w-3 h-3" />
                <span className={`${t.accent} font-medium`}>Authorizations</span>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              {/* Theme Toggle */}
              <div className="flex items-center gap-2" data-testid="theme-toggle">
                <Sun className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-amber-500"}`} />
                <Switch checked={isDark} onCheckedChange={setIsDark} />
                <Moon className={`w-4 h-4 ${isDark ? "text-emerald-400" : "text-slate-400"}`} />
              </div>
              <span className={`text-sm ${t.text}`}>{user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Step Indicator */}
      <div className={`${t.stepBar} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, i) => (
              <div key={step.num} className="flex items-center">
                <div className={`flex items-center gap-2 cursor-pointer`} onClick={() => (isReadOnly || step.num < currentStep) && setCurrentStep(step.num)}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                    step.num < currentStep ? t.stepDone : step.num === currentStep ? t.stepActive : t.stepPending
                  }`}>{step.num < currentStep ? <Check className="w-4 h-4" /> : step.num}</div>
                  <span className={`hidden lg:inline text-xs font-medium ${step.num === currentStep ? t.accent : t.textMuted}`}>{step.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`w-8 xl:w-16 h-0.5 mx-2 ${step.num < currentStep ? t.stepLine : t.stepLinePending}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Read-only banner for approved/locked PAs */}
        {isReadOnly && (
          <div className={`mb-4 rounded-lg border-2 ${
            paStatus === "approved" ? (isDark ? "bg-emerald-500/10 border-emerald-500/40" : "bg-emerald-50 border-emerald-300")
            : paStatus === "denied" ? (isDark ? "bg-red-500/10 border-red-500/40" : "bg-red-50 border-red-300")
            : (isDark ? "bg-slate-700/40 border-slate-600" : "bg-gray-50 border-gray-300")
          } p-4 flex items-center justify-between`} data-testid="readonly-banner">
            <div className="flex items-center gap-3">
              <Badge className={
                paStatus === "approved" ? "bg-emerald-500 text-white"
                : paStatus === "denied" ? "bg-red-500 text-white"
                : "bg-slate-500 text-white"
              }>{paStatus.toUpperCase()}</Badge>
              <div>
                <p className={`text-sm font-semibold ${t.textStrong}`}>This PA is {paStatus} — Read-only mode</p>
                <p className={`text-xs ${t.textMuted}`}>All steps are populated and locked from editing. Navigate freely between steps. Print the PDF for records.</p>
              </div>
            </div>
            <Button onClick={downloadPdf} className={t.btn} data-testid="download-pdf-readonly-btn">
              <FileText className="w-4 h-4 mr-2" /> Download PA PDF
            </Button>
          </div>
        )}

        <fieldset disabled={isReadOnly && currentStep <= 6} className="m-0 p-0 border-0 min-w-0">

        {/* === STEP 1 === */}
        {currentStep === 1 && (
          <div className="space-y-6" data-testid="step-1">
            {/* Patient Search from Connected EHR */}
            {ehrConnection?.connected && (
              <div className={`${t.card} rounded-lg border shadow-sm`}>
                <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Database className={`w-4 h-4 ${t.accent}`} />
                    <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Fertility Coverage Intelligence</h2>
                    <Badge className={`${isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-green-100 text-green-700"} text-[10px]`}>{ehrConnection.ehr_name}</Badge>
                  </div>
                  {importedFromEhr && <Badge className="bg-green-500/20 text-green-500"><Check className="w-3 h-3 mr-1" /> Loaded from EHR</Badge>}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Reset Step 1 to the canonical demo patient (John Doe).
                      // Uses FHIR sample matching the PayerMesh sandbox's known-good record.
                      setPatientFirst("John");
                      setPatientLast("Doe");
                      setPatientDob("1985-01-01");
                      setMemberId("A2CBCBSTX123");
                      setPayer("Blue Cross Blue Shield of Texas");
                      setPlanType("PPO");
                      setDependentFirst("Jane");
                      setDependentLast("Doe");
                      setDependentDob("2015-01-01");
                      setReqProviderName("Provider Name");
                      setReqProviderNpi("1999999984");
                      setBenefitManager("Progyny");
                      setEmployer("Microsoft");
                      setBenefitDesign("Standard");
                      toast.success("Demo patient loaded: John Doe / A2CBCBSTX123");
                    }}
                    className={`${t.border} ${t.btnGhost} text-xs`}
                    data-testid="use-demo-patient-btn"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Use Demo Patient
                  </Button>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className={`text-xs font-semibold ${t.label}`}>First Name</Label>
                      <Input
                        value={searchFirst}
                        onChange={e => setSearchFirst(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && searchPatients()}
                        placeholder="First name"
                        className={`mt-1 ${t.input}`}
                        data-testid="ehr-search-first"
                      />
                    </div>
                    <div>
                      <Label className={`text-xs font-semibold ${t.label}`}>Last Name</Label>
                      <Input
                        value={searchLast}
                        onChange={e => setSearchLast(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && searchPatients()}
                        placeholder="Last name"
                        className={`mt-1 ${t.input}`}
                        data-testid="ehr-search-last"
                      />
                    </div>
                    <div>
                      <Label className={`text-xs font-semibold ${t.label}`}>Date of Birth</Label>
                      <Input
                        type="date"
                        value={searchDob}
                        onChange={e => setSearchDob(e.target.value)}
                        className={`mt-1 ${t.input}`}
                        data-testid="ehr-search-dob"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button onClick={searchPatients} className={`w-full ${t.btn}`} disabled={patientSearching} data-testid="ehr-search-btn">
                        {patientSearching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
                        Search EHR
                      </Button>
                    </div>
                  </div>
                  {/* Patient results */}
                  {patientResults.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                      {patientResults.map((p, i) => (
                        <button
                          type="button"
                          key={p.fhir_id || i}
                          onClick={() => { console.log("Importing patient:", p.fhir_id); importPatient(p); }}
                          className={`w-full text-left p-3 rounded-lg border ${t.border} ${isDark ? "hover:bg-emerald-500/10 hover:border-emerald-500/30" : "hover:bg-blue-50 hover:border-blue-300"} cursor-pointer transition-all flex items-center justify-between`}
                          data-testid={`ehr-result-${p.fhir_id}`}
                          disabled={patientImporting}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full ${isDark ? "bg-emerald-500/20" : "bg-blue-100"} flex items-center justify-center`}>
                              <User className={`w-4 h-4 ${t.accent}`} />
                            </div>
                            <div>
                              <p className={`font-medium ${t.textStrong}`}>{p.full_name || `${p.first_name} ${p.last_name}`}</p>
                              <p className={`text-xs ${t.textMuted}`}>DOB: {p.date_of_birth || "N/A"} | {p.gender || "N/A"}{p.mrn ? ` | MRN: ${p.mrn}` : ""}</p>
                            </div>
                          </div>
                          {patientImporting ? <Loader2 className={`w-4 h-4 ${t.accent} animate-spin`} /> : <ChevronRight className={`w-4 h-4 ${t.textMuted}`} />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No EHR Connected notice */}
            {!ehrLoading && !ehrConnection?.connected && (
              <div className={`${t.warnBg} border rounded-lg p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className={`text-sm font-semibold ${t.textStrong}`}>No EHR Connected</p>
                    <p className={`text-xs ${t.textMuted}`}>Set up EHR integration in Settings to auto-load patient data</p>
                  </div>
                </div>
                <Button onClick={() => navigate("/settings")} variant="outline" size="sm" className={t.btnOutline}>Go to Settings</Button>
              </div>
            )}

            {/* Payer Section */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead}`}><h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Primary Medical Insurance</h2></div>
              <div className="p-6 grid grid-cols-2 gap-4">
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Payer <span className="text-red-500">*</span></Label>
                  {importedFromEhr && payer ? (
                    <Input value={payer} readOnly className={`mt-1 ${t.inputRo}`} data-testid="payer-display" />
                  ) : (
                    <Select value={payer} onValueChange={setPayer}><SelectTrigger className={`mt-1 ${t.input}`} data-testid="payer-select"><SelectValue placeholder="Select or import from EHR..." /></SelectTrigger><SelectContent>{PAYERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
                  )}
                  {importedFromEhr && (
                    <p className={`text-[11px] ${t.textMuted} mt-1`}>Auto-populated from EHR coverage record</p>
                  )}
                </div>
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Plan Type <span className="text-red-500">*</span></Label>
                  {importedFromEhr && planType ? (
                    <Input value={planType} readOnly className={`mt-1 ${t.inputRo}`} data-testid="plan-type-display" />
                  ) : (
                    <Select value={planType} onValueChange={setPlanType}>
                      <SelectTrigger className={`mt-1 ${t.input}`} data-testid="plan-type-select">
                        <SelectValue placeholder="HMO, PPO, EPO..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HMO">HMO</SelectItem>
                        <SelectItem value="PPO">PPO</SelectItem>
                        <SelectItem value="EPO">EPO</SelectItem>
                        <SelectItem value="POS">POS</SelectItem>
                        <SelectItem value="Medicare Advantage">Medicare Advantage</SelectItem>
                        <SelectItem value="Medicaid">Medicaid</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Payer-specific configuration banner */}
                {payerConfig && (
                  <div className={`col-span-2 ${t.infoBg} border rounded-lg p-3 flex items-center justify-between`} data-testid="payer-config-banner">
                    <div className="flex items-center gap-3">
                      <Shield className={`w-4 h-4 ${t.infoText}`} />
                      <div>
                        <p className={`text-xs font-semibold ${t.textStrong}`} data-testid="payer-config-name">
                          {payerConfig.payerName} <span className={`font-mono ${t.textMuted}`}>({payerConfig.payerId})</span>
                        </p>
                        <p className={`text-[11px] ${t.textMuted}`}>
                          Standard turnaround: <strong>{payerConfig.turnaroundDays?.standard}d</strong>
                          {payerConfig.expeditedReviewAvailable && <> · Expedited: <strong>{payerConfig.turnaroundDays?.expedited}d</strong></>}
                          {payerConfig.contactPhone && <> · {payerConfig.contactPhone}</>}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${t.accent} border-current`}>
                      {(payerConfig.requiredFields || []).length} required fields
                    </Badge>
                  </div>
                )}
                {payerConfigLoading && (
                  <div className="col-span-2 flex items-center gap-2 text-xs text-amber-500">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading payer configuration...
                  </div>
                )}
              </div>
            </div>

            {/* Fertility Benefit Section */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Fertility Benefit</h2>
                <p className={`text-xs ${t.textMuted} mt-1`}>Specialty carve-out benefit details</p>
              </div>
              <div className="p-6 grid grid-cols-3 gap-4">
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Benefit Manager</Label>
                  <Input value={benefitManager} onChange={e => setBenefitManager(e.target.value)} className={`mt-1 ${t.input}`} data-testid="benefit-manager" />
                </div>
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Employer</Label>
                  <Input value={employer} onChange={e => setEmployer(e.target.value)} className={`mt-1 ${t.input}`} data-testid="employer" />
                </div>
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Benefit Design</Label>
                  <Input value={benefitDesign} onChange={e => setBenefitDesign(e.target.value)} className={`mt-1 ${t.input}`} data-testid="benefit-design" />
                </div>
              </div>
            </div>

            {/* Member Info */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Member Information</h2>
                <p className={`text-xs ${t.textMuted} mt-1`}>All fields are required unless specified as optional.</p>
              </div>
              <div className="p-6 grid grid-cols-2 gap-4">
                <div><Label className={`text-xs font-semibold ${t.label}`}>Member ID *</Label><Input value={memberId} onChange={e => setMemberId(e.target.value)} placeholder="Enter member ID" className={`mt-1 ${t.input}`} data-testid="member-id" /></div>
                <div><Label className={`text-xs font-semibold ${t.label}`}>Relationship to Subscriber *</Label><Select value={relationship} onValueChange={setRelationship}><SelectTrigger className={`mt-1 ${t.input}`}><SelectValue /></SelectTrigger><SelectContent>{RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className={`text-xs font-semibold ${t.label}`}>Patient First Name *</Label><Input value={patientFirst} onChange={e => setPatientFirst(e.target.value)} placeholder="First name" className={`mt-1 ${t.input}`} data-testid="patient-first" /></div>
                <div><Label className={`text-xs font-semibold ${t.label}`}>Patient Last Name *</Label><Input value={patientLast} onChange={e => setPatientLast(e.target.value)} placeholder="Last name" className={`mt-1 ${t.input}`} data-testid="patient-last" /></div>
                <div><Label className={`text-xs font-semibold ${t.label}`}>Patient Date of Birth *</Label><Input type="date" value={patientDob} onChange={e => setPatientDob(e.target.value)} className={`mt-1 ${t.input}`} data-testid="patient-dob" /></div>
                <div><Label className={`text-xs font-semibold ${t.label}`}>Patient Gender *</Label>
                  <Select value={patientGender} onValueChange={setPatientGender}><SelectTrigger className={`mt-1 ${t.input}`} data-testid="patient-gender"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Requesting Provider — search + retrieve flow */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                <div>
                  <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Requesting Provider</h2>
                  <p className={`text-xs ${t.textMuted} mt-0.5`}>
                    Search by NPI or Role Code, then click <strong>Retrieve Provider Information</strong> to auto-populate the form.
                  </p>
                </div>
                {providerFound && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={t.btnGhost}
                    onClick={() => { setProviderFound(false); }}
                    data-testid="provider-search-again-btn"
                  >
                    <Search className="w-3.5 h-3.5 mr-1" /> Search Again
                  </Button>
                )}
              </div>
              <div className="p-6 space-y-4">
                {/* Search panel (3 fields + retrieve button) */}
                <div className="grid grid-cols-4 gap-3 items-end" data-testid="provider-search-panel">
                  <div>
                    <Label className={`text-xs font-semibold ${t.label}`}>Search By</Label>
                    <Select value={reqProviderSearchBy} onValueChange={setReqProviderSearchBy}>
                      <SelectTrigger className={`mt-1 ${t.input}`} data-testid="provider-search-by"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NPI">NPI</SelectItem>
                        <SelectItem value="Name">Name</SelectItem>
                        <SelectItem value="Tax ID">Tax ID</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={`text-xs font-semibold ${t.label}`}>Role Code</Label>
                    <Select value={reqProviderRoleCode} onValueChange={setReqProviderRoleCode}>
                      <SelectTrigger className={`mt-1 ${t.input}`} data-testid="provider-role-code"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLE_CODES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={`text-xs font-semibold ${t.label}`}>NPI #</Label>
                    <Input
                      value={reqProviderNpi}
                      onChange={e => setReqProviderNpi(e.target.value)}
                      placeholder="10-digit NPI"
                      className={`mt-1 ${t.input}`}
                      data-testid="req-provider-npi"
                    />
                  </div>
                  <div>
                    <Button
                      onClick={retrieveProviderInfo}
                      disabled={providerSearching || !reqProviderNpi}
                      className={`w-full ${t.btn}`}
                      data-testid="retrieve-provider-btn"
                    >
                      {providerSearching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                      Retrieve Provider Information
                    </Button>
                  </div>
                </div>

                {/* Populated fields shown after Retrieve */}
                {providerFound && (
                  <div className="space-y-4 pt-2" data-testid="provider-details">
                    <div className={`${t.successBg} border rounded-md p-2 text-xs ${t.successText} flex items-center gap-2`}>
                      <Check className="w-3.5 h-3.5" /> Provider found · Edit fields below as needed.
                    </div>

                    {/* First Name / Last Name */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>First Name</Label>
                        <Input value={reqProviderName.split(" ")[0] || ""} onChange={e => {
                          const parts = reqProviderName.split(" ");
                          parts[0] = e.target.value;
                          setReqProviderName(parts.join(" "));
                        }} className={`mt-1 ${t.input}`} data-testid="provider-first-name" />
                      </div>
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Last Name</Label>
                        <Input value={reqProviderName.split(" ").slice(1).join(" ") || ""} onChange={e => {
                          const first = reqProviderName.split(" ")[0] || "";
                          setReqProviderName(`${first} ${e.target.value}`.trim());
                        }} className={`mt-1 ${t.input}`} data-testid="provider-last-name" />
                      </div>
                    </div>

                    {/* Tax ID */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Tax ID</Label>
                        <Input value={reqProviderTaxId} onChange={e => setReqProviderTaxId(e.target.value)} className={`mt-1 ${t.input}`} />
                      </div>
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Specialty / Taxonomy</Label>
                        <Select value={reqProviderSpecialty} onValueChange={setReqProviderSpecialty}>
                          <SelectTrigger className={`mt-1 ${t.input}`}><SelectValue placeholder="Select specialty..." /></SelectTrigger>
                          <SelectContent>
                            {["Internal Medicine", "Family Medicine", "General Surgery", "Orthopedic Surgery", "Cardiology", "Oncology", "Neurology", "Pulmonology", "Gastroenterology", "Radiology", "Psychiatry", "Dermatology", "Ophthalmology", "Urology", "OB/GYN", "Pediatrics", "Physical Therapy", "Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Address Line 1 */}
                    <div>
                      <Label className={`text-xs font-semibold ${t.label}`}>Address Line 1</Label>
                      <Input value={reqProviderAddr} onChange={e => setReqProviderAddr(e.target.value)} className={`mt-1 ${t.input}`} />
                    </div>

                    {/* City / State / ZIP */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>City</Label>
                        <Input value={reqProviderCity} onChange={e => setReqProviderCity(e.target.value)} className={`mt-1 ${t.input}`} />
                      </div>
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>State</Label>
                        <Select value={reqProviderState} onValueChange={setReqProviderState}>
                          <SelectTrigger className={`mt-1 ${t.input}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>
                            {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>ZIP Code</Label>
                        <Input value={reqProviderZip} onChange={e => setReqProviderZip(e.target.value)} className={`mt-1 ${t.input}`} />
                      </div>
                    </div>

                    <Separator className={t.border} />

                    {/* Contact Information Section */}
                    <h3 className={`text-xs font-bold ${t.cardHeadText} uppercase`}>Contact Information</h3>

                    <div>
                      <Label className={`text-xs font-semibold ${t.label}`}>Contact Name</Label>
                      <Input value={contactName} onChange={e => setContactName(e.target.value)} className={`mt-1 ${t.input}`} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Contact Phone</Label>
                        <Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="(555) 555-5555" className={`mt-1 ${t.input}`} />
                      </div>
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Contact Extension <span className={t.textMuted}>(optional)</span></Label>
                        <Input value={contactExt} onChange={e => setContactExt(e.target.value)} className={`mt-1 ${t.input}`} />
                      </div>
                    </div>

                    <div>
                      <Label className={`text-xs font-semibold ${t.label}`}>Confidential Voicemail <span className="text-red-400">*</span></Label>
                      <Select value={confidentialVoicemail} onValueChange={setConfidentialVoicemail}>
                        <SelectTrigger className={`mt-1 ${t.input}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes - This contact number has a confidential voicemail</SelectItem>
                          <SelectItem value="no">No - This contact number is not a confidential voicemail</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Contact Fax</Label>
                        <Input value={contactFax} onChange={e => setContactFax(e.target.value)} className={`mt-1 ${t.input}`} />
                      </div>
                      <div>
                        <Label className={`text-xs font-semibold ${t.label}`}>Contact Email Address</Label>
                        <Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} className={`mt-1 ${t.input}`} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Eligibility & Coverage Verification — now AFTER requesting provider */}
            <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="eligibility-card">
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className={`w-5 h-5 ${t.accent}`} />
                  <div>
                    <p className={`text-sm font-semibold ${t.textStrong}`}>Eligibility & Coverage Verification</p>
                    <p className={`text-xs ${t.textMuted}`}>3-step verification against {payer || "selected payer"} · Availity Coverages</p>
                  </div>
                </div>
                <Button
                  onClick={() => { checkEligibility(); handleVerifyEligibility(); }}
                  className={`${isDark ? "bg-cyan-600 hover:bg-cyan-500" : "bg-teal-600 hover:bg-teal-500"} text-white`}
                  disabled={(eligibilityLoading || verifying) || !memberId || !patientFirst || !patientLast || !payer}
                  data-testid="check-eligibility-btn"
                >
                  {(eligibilityLoading || verifying) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                  Verify Eligibility & Coverage
                </Button>
              </div>

              {/* 3-Step Verification UI */}
              {showEligibility && (
                <div className={`mx-4 mb-4 rounded-lg border ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"} p-4 space-y-3`}>
                  {[
                    { key: "member", label: "1. Checking Member Eligibility" },
                    { key: "plan", label: "2. Verifying Member Plan" },
                    { key: "provider", label: "3. Verifying Provider Contract Status" },
                  ].map(step => {
                    const s = eligSteps[step.key];
                    const icon = s.status === "running" ? <Loader2 className={`w-4 h-4 ${t.accent} animate-spin`} />
                      : s.status === "success" ? <Check className="w-4 h-4 text-green-500" />
                      : s.status === "fail" ? <AlertCircle className="w-4 h-4 text-red-500" />
                      : <div className={`w-4 h-4 rounded-full border ${t.border}`} />;
                    return (
                      <div key={step.key} className={`flex items-start gap-3 p-3 rounded border ${t.border} ${isDark ? "bg-slate-900/40" : "bg-white"}`} data-testid={`elig-step-${step.key}`}>
                        <div className="mt-0.5">{icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-semibold ${t.textStrong}`}>{step.label}</p>
                            <Badge variant="outline" className={`text-[10px] ${
                              s.status === "success" ? "text-green-500 border-green-500/30"
                              : s.status === "fail" ? "text-red-500 border-red-500/30"
                              : s.status === "running" ? `${t.accent} border-current`
                              : `${t.textMuted} border-current`
                            }`}>
                              {s.status === "running" ? "Running..."
                                : s.status === "success" ? "Verified"
                                : s.status === "fail" ? "Failed"
                                : "Pending"}
                            </Badge>
                          </div>
                          {s.detail && <p className={`text-xs ${t.textMuted} mt-1`} data-testid={`elig-step-${step.key}-detail`}>{s.detail}</p>}
                          {s.status === "running" && (
                            <div className={`mt-2 h-1 w-full rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-200"}`}>
                              <div className={`h-full ${isDark ? "bg-emerald-500" : "bg-[#0066a1]"} animate-pulse`} style={{ width: "60%" }} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Final benefit detail panel — only after all steps complete */}
                  {eligibilityData && !eligibilityLoading && (
                    <div className={`mt-2 rounded-lg border ${t.border} ${isDark ? "bg-slate-900/60" : "bg-white"} p-3 space-y-3`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className={`w-4 h-4 ${eligibilityData.eligible ? "text-green-500" : "text-red-500"}`} />
                          <span className={`text-sm font-semibold ${t.textStrong}`}>Coverage Summary</span>
                          {eligibilityData.source === "availity_live" && <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/30">LIVE</Badge>}
                        </div>
                        <Badge className={eligibilityData.eligible ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}>
                          {eligibilityData.eligible ? "Eligible" : "Not Eligible"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div><span className={t.textMuted}>Plan:</span> <strong className={t.textStrong}>{eligibilityData.subscriber?.plan_name}</strong></div>
                        <div><span className={t.textMuted}>Status:</span> <strong className="text-green-500">{eligibilityData.plan_status}</strong></div>
                        <div><span className={t.textMuted}>Group:</span> <strong className={t.textStrong}>{eligibilityData.subscriber?.group_number}</strong></div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {eligibilityData.benefits?.filter(b => b.description).slice(0, 6).map((b, i) => (
                          <div key={i} className={`p-2 rounded ${isDark ? "bg-slate-900/70" : "bg-gray-50"} border ${t.border} text-xs`}>
                            <p className={`font-semibold ${t.text}`}>{b.service_type}</p>
                            <p className={`font-bold ${t.textStrong}`}>{b.amount !== undefined ? `$${b.amount.toFixed(2)}` : b.percent ? `${b.percent}%` : "Covered"}</p>
                            <p className={t.textMuted}>{b.description}</p>
                            {b.remaining !== undefined && <p className="text-amber-500">Remaining: ${b.remaining.toFixed(2)}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Coverage & Benefit Intelligence Snapshot (PayerMesh + Progyny) */}
            {(coverageSummary || verifying || verifyError) && (
              <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="coverage-snapshot">
                <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                  <div>
                    <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Coverage &amp; Benefit Intelligence Snapshot</h2>
                    {verificationTimestamp && (
                      <p className={`text-xs ${t.textMuted} mt-1`}>Verified at {new Date(verificationTimestamp).toLocaleString()} via PayerMesh FHIR</p>
                    )}
                  </div>
                  {verifying && <Badge className="bg-amber-500/20 text-amber-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Verifying medical eligibility and fertility benefits...</Badge>}
                </div>

                {verifyError && (
                  <div className="p-4">
                    <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500" data-testid="verify-error">
                      Unable to verify eligibility at this time. Please review member and payer details or try again.
                    </div>
                    <button type="button" onClick={() => setVerifyDebugOpen(o => !o)} className={`mt-2 text-xs underline ${t.textMuted}`} data-testid="toggle-verify-debug">
                      {verifyDebugOpen ? "Hide" : "Show"} technical details
                    </button>
                    {verifyDebugOpen && (
                      <pre className={`mt-2 text-[11px] font-mono p-2 rounded ${isDark ? "bg-slate-900" : "bg-gray-50"} border ${t.border} overflow-x-auto`}>{verifyError}</pre>
                    )}
                  </div>
                )}

                {coverageSummary && (() => {
                  const cs = coverageSummary || {};
                  const benefits = cs.benefits || {};
                  // Concatenate multiple values from each benefit array (in-network + out-of-network,
                  // individual + family) so admins see the full picture, not just the first item.
                  const joinAmounts = (arr, valueFn) => {
                    const parts = (arr || []).map(it => {
                      const v = valueFn(it);
                      if (!v) return null;
                      const ctx = [it.network, it.level, it.timePeriod, it.serviceType].filter(Boolean).slice(0, 2).join(" / ");
                      return ctx ? `${v} (${ctx})` : v;
                    }).filter(Boolean);
                    return parts.length ? parts.join(" • ") : "";
                  };
                  const deductibleStr = joinAmounts(benefits.deductibles, it => it.amount && `$${Number(it.amount).toLocaleString()}`);
                  const deductibleRemainingStr = joinAmounts(benefits.deductibles, it => (it.remaining !== undefined && it.remaining !== null) && `$${Number(it.remaining).toLocaleString()}`);
                  const oopStr = joinAmounts(benefits.outOfPocket, it => it.amount && `$${Number(it.amount).toLocaleString()}`);
                  const oopRemainingStr = joinAmounts(benefits.outOfPocket, it => (it.remaining !== undefined && it.remaining !== null) && `$${Number(it.remaining).toLocaleString()}`);
                  const coinsStr = joinAmounts(benefits.coinsurance, it => it.percent && `${it.percent}%`);
                  const copayStr = joinAmounts(benefits.copays, it => it.amount && `$${Number(it.amount).toLocaleString()}`);
                  const active = (benefits.activeCoverage || []).length > 0;
                  const planName = (cs.plan?.planInformation || {}).planName || planType || payer;
                  const fmt = (v, fallback = "Requires Verification") => (v !== undefined && v !== null && v !== "" ? String(v) : fallback);
                  const progyny = progynySummary || {};

                  const rows = [
                    { label: "Coverage Status", medical: active ? "Active" : "Requires Verification", progyny: "Active" },
                    { label: "Member ID", medical: cs.member?.subscriber?.memberId || memberId || "—", progyny: progyny.member_id_progyny || "Requires Benefit Manager Verification" },
                    { label: "Payer / Benefit Manager", medical: payer || cs.payer?.name || "—", progyny: progyny.benefit_manager || "Progyny" },
                    { label: "Eligibility Status", medical: active ? "Active" : "Inactive / Verify", progyny: "Active" },
                    { label: "Plan Type", medical: fmt(planName), progyny: "Employer-sponsored fertility benefit" },
                    { label: "Deductible", medical: fmt(deductibleStr), progyny: "Not applicable" },
                    { label: "Remaining Deductible", medical: fmt(deductibleRemainingStr), progyny: "Not applicable" },
                    { label: "Out-of-Pocket Maximum", medical: fmt(oopStr), progyny: "If applicable" },
                    { label: "Out-of-Pocket Remaining", medical: fmt(oopRemainingStr), progyny: "If applicable" },
                    { label: "Coinsurance", medical: fmt(coinsStr), progyny: "If applicable" },
                    { label: "Copays", medical: fmt(copayStr), progyny: "If applicable" },
                    { label: "IVF Coverage", medical: "Requires Validation", progyny: progyny.ivf_coverage || "Covered" },
                    { label: "IUI Coverage", medical: "Requires Validation", progyny: progyny.iui_coverage || "Covered" },
                    { label: "Fertility Medication Coverage", medical: "Refer to PBM / Medical if applicable", progyny: progyny.fertility_medication_coverage || "Covered via Progyny Rx" },
                    { label: "Genetic Testing / PGT", medical: "Requires Validation", progyny: progyny.genetic_testing_pgt_coverage || "Covered" },
                    { label: "Embryo Storage Coverage", medical: "Requires Validation", progyny: progyny.embryo_storage_coverage || "Covered" },
                    { label: "Prior Authorization Required", medical: "Likely", progyny: progyny.prior_authorization || "Yes" },
                    { label: "Network Requirement", medical: "In-network preferred", progyny: progyny.network_requirement || "Progyny network required" },
                  ];
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="coverage-table">
                        <thead>
                          <tr className={`${isDark ? "bg-slate-900/60" : "bg-gray-50"} border-b ${t.border}`}>
                            <th className={`text-left px-4 py-2 text-xs font-bold uppercase ${t.textMuted}`}>Benefit / Coverage Category</th>
                            <th className={`text-left px-4 py-2 text-xs font-bold uppercase ${t.textMuted}`}>Medical Benefits / Coverage</th>
                            <th className={`text-left px-4 py-2 text-xs font-bold uppercase ${t.textMuted}`}>Progyny Benefits / Coverage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={r.label} className={`border-b ${t.border} ${i % 2 === 0 ? (isDark ? "bg-slate-900/30" : "bg-white") : (isDark ? "bg-slate-900/10" : "bg-gray-50/40")}`}>
                              <td className={`px-4 py-2 font-semibold ${t.text}`}>{r.label}</td>
                              <td className={`px-4 py-2 ${t.textMuted}`}>{r.medical}</td>
                              <td className={`px-4 py-2 ${t.textMuted}`}>{r.progyny}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* Plan Financials & Benefit Details — comprehensive breakdown */}
                {coverageSummary && (() => {
                  const b = coverageSummary.benefits || {};
                  const fmtMoney = (v) => v === undefined || v === null || v === "" ? "—" : (typeof v === "number" ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : String(v));
                  const fmtPercent = (v) => v === undefined || v === null || v === "" ? "—" : `${v}%`;
                  // Helper to render a benefit array as a sub-table. Inlined as a function
                  // (not a component) to avoid React's "no-unstable-nested-components" warning.
                  const renderBenefitTable = (title, items, columns, testid) => {
                    if (!items || items.length === 0) return null;
                    return (
                      <div className="mb-4" data-testid={testid}>
                        <h4 className={`text-xs font-bold uppercase ${t.textMuted} mb-2 flex items-center gap-2`}>
                          {title} <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
                        </h4>
                        <div className="overflow-x-auto">
                          <table className={`w-full text-xs border ${t.border} rounded`}>
                            <thead>
                              <tr className={`${isDark ? "bg-slate-900/40" : "bg-gray-50"}`}>
                                {columns.map(c => (
                                  <th key={c.key} className={`text-left px-3 py-2 font-semibold ${t.textMuted}`}>{c.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((it, i) => (
                                <tr key={i} className={`border-t ${t.border} ${i % 2 === 0 ? (isDark ? "bg-slate-900/20" : "bg-white") : (isDark ? "bg-slate-900/5" : "bg-gray-50/30")}`}>
                                  {columns.map(c => (
                                    <td key={c.key} className={`px-3 py-1.5 ${t.text}`}>{c.format ? c.format(it[c.key], it) : (it[c.key] ?? "—")}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  };

                  const hasAny = ["activeCoverage", "deductibles", "copays", "coinsurance", "outOfPocket", "limitations", "benefitDescriptions", "disclaimers", "other"].some(k => (b[k] || []).length);

                  return (
                    <div className={`px-4 py-4 border-t ${t.border}`}>
                      <h3 className={`text-sm font-bold ${t.textStrong} uppercase tracking-wide mb-3 flex items-center gap-2`}>
                        <Shield className={`w-4 h-4 ${t.accent}`} />
                        Plan Financials &amp; Benefit Details
                      </h3>

                      {!hasAny && (
                        <div className={`p-3 rounded text-xs ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-800"} flex items-start gap-2 mb-3`} data-testid="no-benefits-banner">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold mb-1">Payer returned no specific benefit details</p>
                            <p>This typically means the member ID or DOB doesn&apos;t match the payer&apos;s records.</p>
                            <p className="mt-2"><strong>What we sent:</strong> Member <span className="font-mono">{memberId || "—"}</span>, DOB <span className="font-mono">{patientDob || "—"}</span>, Patient <span className="font-mono">{patientFirst} {patientLast}</span></p>
                            <p className="mt-1"><strong>Expected for demo:</strong> Member <span className="font-mono">A2CBCBSTX123</span>, DOB <span className="font-mono">1985-01-01</span>, Patient <span className="font-mono">John Doe</span></p>
                          </div>
                        </div>
                      )}

                      {renderBenefitTable("Active Coverage", b.activeCoverage, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "name", label: "Coverage" },
                        { key: "planCoverage", label: "Plan" },
                        { key: "network", label: "Network" },
                      ], "bt-active-coverage")}

                      {renderBenefitTable("Deductibles", b.deductibles, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "network", label: "Network" },
                        { key: "timePeriod", label: "Period" },
                        { key: "level", label: "Level" },
                        { key: "amount", label: "Amount", format: fmtMoney },
                        { key: "remaining", label: "Remaining", format: fmtMoney },
                      ], "bt-deductibles")}

                      {renderBenefitTable("Out-of-Pocket Maximum", b.outOfPocket, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "network", label: "Network" },
                        { key: "timePeriod", label: "Period" },
                        { key: "level", label: "Level" },
                        { key: "amount", label: "Amount", format: fmtMoney },
                        { key: "remaining", label: "Remaining", format: fmtMoney },
                      ], "bt-outofpocket")}

                      {renderBenefitTable("Copays", b.copays, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "placeOfService", label: "Place of Service" },
                        { key: "network", label: "Network" },
                        { key: "amount", label: "Copay", format: fmtMoney },
                      ], "bt-copays")}

                      {renderBenefitTable("Coinsurance", b.coinsurance, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "placeOfService", label: "Place of Service" },
                        { key: "network", label: "Network" },
                        { key: "percent", label: "Patient Pays %", format: fmtPercent },
                      ], "bt-coinsurance")}

                      {renderBenefitTable("Benefit Descriptions", b.benefitDescriptions, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "description", label: "Description" },
                        { key: "network", label: "Network" },
                      ], "bt-benefit-descriptions")}

                      {renderBenefitTable("Limitations", b.limitations, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "quantity", label: "Quantity" },
                        { key: "timePeriod", label: "Period" },
                        { key: "description", label: "Description" },
                      ], "bt-limitations")}

                      {renderBenefitTable("Disclaimers", b.disclaimers, [
                        { key: "description", label: "Disclaimer" },
                      ], "bt-disclaimers")}

                      {renderBenefitTable("Other Benefit Info", b.other, [
                        { key: "serviceType", label: "Service Type" },
                        { key: "description", label: "Description" },
                        { key: "network", label: "Network" },
                      ], "bt-other")}

                      {/* Quick at-a-glance financial roll-up */}
                      {(() => {
                        const sumAmount = (arr, key = "amount") => (arr || []).reduce((acc, x) => acc + (Number(x?.[key]) || 0), 0);
                        const totDed = sumAmount(b.deductibles);
                        const totDedRem = sumAmount(b.deductibles, "remaining");
                        const totOop = sumAmount(b.outOfPocket);
                        const totOopRem = sumAmount(b.outOfPocket, "remaining");
                        if (!totDed && !totOop) return null;
                        return (
                          <div className={`mt-4 p-3 rounded border-l-4 border-l-blue-500 ${isDark ? "bg-slate-900/40" : "bg-blue-50/40"}`} data-testid="financial-rollup">
                            <p className={`text-xs ${t.textMuted} uppercase font-bold mb-1`}>Quick Financial Roll-Up</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                              <div><span className={t.textMuted}>Total Deductible:</span> <strong className={t.textStrong}>{fmtMoney(totDed)}</strong></div>
                              <div><span className={t.textMuted}>Deductible Remaining:</span> <strong className={t.textStrong}>{fmtMoney(totDedRem)}</strong></div>
                              <div><span className={t.textMuted}>Total OOP Max:</span> <strong className={t.textStrong}>{fmtMoney(totOop)}</strong></div>
                              <div><span className={t.textMuted}>OOP Remaining:</span> <strong className={t.textStrong}>{fmtMoney(totOopRem)}</strong></div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* IVF Coverage Intelligence Summary Card */}
            {intelligenceSummary && (
              <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="ivf-intelligence-card">
                <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                  <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>IVF Coverage Intelligence Summary</h2>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><span className={t.textMuted}>Medical Coverage:</span> <strong className={t.textStrong} data-testid="intel-medical">{intelligenceSummary.medical_coverage}</strong></div>
                  <div><span className={t.textMuted}>Progyny Benefit:</span> <strong className={t.textStrong} data-testid="intel-progyny">{intelligenceSummary.progyny_benefit}</strong></div>
                  <div><span className={t.textMuted}>IVF Coverage:</span> <strong className={t.textStrong} data-testid="intel-ivf">{intelligenceSummary.ivf_coverage}</strong></div>
                  <div><span className={t.textMuted}>Fertility Medication:</span> <strong className={t.textStrong} data-testid="intel-medication">{intelligenceSummary.fertility_medication_coverage}</strong></div>
                  <div><span className={t.textMuted}>PA Required:</span> <strong className={t.textStrong} data-testid="intel-pa">{intelligenceSummary.pa_required}</strong></div>
                  <div className="md:col-span-2"><span className={t.textMuted}>Network Requirement:</span> <strong className={t.textStrong} data-testid="intel-network">{intelligenceSummary.network_requirement}</strong></div>
                  <div className={`md:col-span-2 mt-2 p-3 rounded border-l-4 border-l-emerald-500 ${isDark ? "bg-slate-900/40" : "bg-emerald-50/60"}`}>
                    <p className={`text-xs ${t.textMuted} uppercase font-bold mb-1`}>Recommended Next Action</p>
                    <p className={`text-sm ${t.text}`} data-testid="intel-next-action">{intelligenceSummary.recommended_next_action}</p>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* === STEP 2 === */}
        {currentStep === 2 && (
          <div className="space-y-6" data-testid="step-2">

            {/* Section A — Clinical Context */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Clinical Context</h2>
                <p className={`text-xs ${t.textMuted} mt-1`}>Categorize the encounter to drive code recommendations.</p>
              </div>
              <div className="p-6 grid grid-cols-2 gap-4">
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Clinical Category</Label>
                  <Select value={clinicalCategory} onValueChange={setClinicalCategory}>
                    <SelectTrigger className={`mt-1 ${t.input}`} data-testid="clinical-category"><SelectValue placeholder="Select category..." /></SelectTrigger>
                    <SelectContent>{FERTILITY_CLINICAL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`text-xs font-semibold ${t.label} uppercase`}>Specialty</Label>
                  <Select value={clinicalSpecialty} onValueChange={setClinicalSpecialty}>
                    <SelectTrigger className={`mt-1 ${t.input}`} data-testid="clinical-specialty"><SelectValue placeholder="Select specialty..." /></SelectTrigger>
                    <SelectContent>{FERTILITY_SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section B — Recommended Services, ICD-10, CPT */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Recommended Services, ICD-10 &amp; CPT</h2>
                <p className={`text-xs ${t.textMuted} mt-1`}>Select all applicable items — they auto-populate the financial breakdown below.</p>
              </div>
              <div className="p-6 space-y-6">
                {/* Recommended Services checkboxes */}
                <div>
                  <h3 className={`text-xs font-bold uppercase ${t.textMuted} mb-2`}>Recommended Services</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {RECOMMENDED_FERTILITY_SERVICES.map(svc => {
                      const checked = recommendedServices.includes(svc);
                      return (
                        <label key={svc} className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-xs ${checked ? (isDark ? "bg-emerald-500/10 border-emerald-500/40" : "bg-emerald-50 border-emerald-300") : t.border}`} data-testid={`service-${svc.replace(/\s+/g, "-").toLowerCase()}`}>
                          <input type="checkbox" checked={checked} onChange={() => setRecommendedServices(prev => checked ? prev.filter(s => s !== svc) : [...prev, svc])} />
                          <span className={t.text}>{svc}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Recommended ICD-10 table */}
                <div>
                  <h3 className={`text-xs font-bold uppercase ${t.textMuted} mb-2`}>Recommended ICD-10 Codes</h3>
                  <div className="overflow-x-auto">
                    <table className={`w-full text-xs border ${t.border} rounded`}>
                      <thead>
                        <tr className={`${isDark ? "bg-slate-900/40" : "bg-gray-50"}`}>
                          <th className={`text-left px-3 py-2 w-12 ${t.textMuted}`}>Select</th>
                          <th className={`text-left px-3 py-2 ${t.textMuted}`}>ICD-10</th>
                          <th className={`text-left px-3 py-2 ${t.textMuted}`}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {RECOMMENDED_FERTILITY_ICD.map(row => {
                          const checked = recommendedIcd.includes(row.code);
                          return (
                            <tr key={row.code} className={`border-t ${t.border}`} data-testid={`icd-row-${row.code}`}>
                              <td className="px-3 py-1.5"><input type="checkbox" checked={checked} onChange={() => setRecommendedIcd(prev => checked ? prev.filter(c => c !== row.code) : [...prev, row.code])} /></td>
                              <td className={`px-3 py-1.5 font-mono ${t.textStrong}`}>{row.code}</td>
                              <td className={`px-3 py-1.5 ${t.text}`}>{row.description}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recommended CPT table */}
                <div>
                  <h3 className={`text-xs font-bold uppercase ${t.textMuted} mb-2`}>Recommended CPT Codes</h3>
                  <div className="overflow-x-auto">
                    <table className={`w-full text-xs border ${t.border} rounded`}>
                      <thead>
                        <tr className={`${isDark ? "bg-slate-900/40" : "bg-gray-50"}`}>
                          <th className={`text-left px-3 py-2 w-12 ${t.textMuted}`}>Select</th>
                          <th className={`text-left px-3 py-2 ${t.textMuted}`}>CPT</th>
                          <th className={`text-left px-3 py-2 ${t.textMuted}`}>Description</th>
                          <th className={`text-left px-3 py-2 ${t.textMuted}`}>PA Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {RECOMMENDED_FERTILITY_CPT.map(row => {
                          const checked = recommendedCpt.includes(row.code);
                          const paBadge = row.paStatus === "Review" ? "bg-amber-500/20 text-amber-600" : row.paStatus === "Yes" ? "bg-red-500/20 text-red-600" : "bg-emerald-500/20 text-emerald-600";
                          return (
                            <tr key={row.code} className={`border-t ${t.border}`} data-testid={`cpt-row-${row.code}`}>
                              <td className="px-3 py-1.5"><input type="checkbox" checked={checked} onChange={() => setRecommendedCpt(prev => checked ? prev.filter(c => c !== row.code) : [...prev, row.code])} /></td>
                              <td className={`px-3 py-1.5 font-mono ${t.textStrong}`}>{row.code}</td>
                              <td className={`px-3 py-1.5 ${t.text}`}>{row.description}</td>
                              <td className="px-3 py-1.5"><Badge className={`${paBadge} text-[10px]`}>{row.paStatus}</Badge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Section C — Financial Breakdown */}
            {(() => {
              const selectedCpts = RECOMMENDED_FERTILITY_CPT.filter(c => recommendedCpt.includes(c.code));
              const totalCost = selectedCpts.reduce((a, c) => a + c.allowed, 0);
              const totalPatient = selectedCpts.reduce((a, c) => a + c.patient, 0);
              const totalPlan = selectedCpts.reduce((a, c) => a + c.plan, 0);
              return (
                <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="financial-breakdown">
                  <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                    <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase tracking-wide`}>Financial Breakdown</h2>
                    <p className={`text-xs ${t.textMuted} mt-1`}>Estimated Cost Analysis — based on selected CPT codes &amp; current eligibility.</p>
                  </div>
                  <div className="p-6 space-y-5">
                    {/* Selected Services table */}
                    <div>
                      <h3 className={`text-xs font-bold uppercase ${t.textMuted} mb-2`}>Selected Services</h3>
                      {selectedCpts.length === 0 ? (
                        <p className={`text-xs ${t.textMuted} italic`}>No CPT codes selected yet. Select rows above to populate this table.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className={`w-full text-xs border ${t.border} rounded`}>
                            <thead>
                              <tr className={`${isDark ? "bg-slate-900/40" : "bg-gray-50"}`}>
                                <th className={`text-left px-3 py-2 ${t.textMuted}`}>CPT</th>
                                <th className={`text-left px-3 py-2 ${t.textMuted}`}>Description</th>
                                <th className={`text-right px-3 py-2 ${t.textMuted}`}>Allowed Amount</th>
                                <th className={`text-right px-3 py-2 ${t.textMuted}`}>Patient Resp.</th>
                                <th className={`text-right px-3 py-2 ${t.textMuted}`}>Plan Resp.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedCpts.map(c => (
                                <tr key={c.code} className={`border-t ${t.border}`}>
                                  <td className={`px-3 py-1.5 font-mono ${t.textStrong}`}>{c.code}</td>
                                  <td className={`px-3 py-1.5 ${t.text}`}>{c.description}</td>
                                  <td className={`px-3 py-1.5 text-right ${t.text}`}>${c.allowed}</td>
                                  <td className={`px-3 py-1.5 text-right text-amber-500`}>${c.patient}</td>
                                  <td className={`px-3 py-1.5 text-right text-emerald-500`}>${c.plan}</td>
                                </tr>
                              ))}
                              <tr className={`border-t-2 ${t.border} ${isDark ? "bg-slate-900/40" : "bg-gray-50"}`}>
                                <td colSpan={2} className={`px-3 py-2 font-bold ${t.textStrong}`}>Total</td>
                                <td className={`px-3 py-2 text-right font-bold ${t.textStrong}`}>${totalCost}</td>
                                <td className={`px-3 py-2 text-right font-bold text-amber-500`}>${totalPatient}</td>
                                <td className={`px-3 py-2 text-right font-bold text-emerald-500`}>${totalPlan}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Coverage Summary */}
                    <div>
                      <h3 className={`text-xs font-bold uppercase ${t.textMuted} mb-2`}>Coverage Summary</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                        <div className={`p-3 rounded border ${t.border}`}><span className={t.textMuted}>Estimated Total Cost</span><div className={`font-bold ${t.textStrong} text-base`}>${totalCost}</div></div>
                        <div className={`p-3 rounded border ${t.border}`}><span className={t.textMuted}>Plan Responsibility</span><div className={`font-bold text-emerald-500 text-base`}>${totalPlan}</div></div>
                        <div className={`p-3 rounded border ${t.border}`}><span className={t.textMuted}>Patient Responsibility</span><div className={`font-bold text-amber-500 text-base`}>${totalPatient}</div></div>
                        <div className={`p-3 rounded border ${t.border}`}><span className={t.textMuted}>Remaining Deductible</span><div className={`font-bold ${t.textStrong} text-base`}>$500</div></div>
                        <div className={`p-3 rounded border ${t.border}`}><span className={t.textMuted}>Coinsurance</span><div className={`font-bold ${t.textStrong} text-base`}>20%</div></div>
                        <div className={`p-3 rounded border ${t.border}`}><span className={t.textMuted}>OOP Remaining</span><div className={`font-bold ${t.textStrong} text-base`}>$2,100</div></div>
                      </div>
                    </div>

                    {/* Financial Notes */}
                    <div className={`p-3 rounded border-l-4 border-l-blue-500 ${isDark ? "bg-slate-900/40" : "bg-blue-50/40"}`}>
                      <p className={`text-xs ${t.textMuted} uppercase font-bold mb-1`}>Financial Notes</p>
                      <ul className={`text-xs ${t.text} list-disc ml-5 space-y-0.5`}>
                        <li>Estimates are based on current eligibility and benefit information.</li>
                        <li>Final patient responsibility may vary depending on claim adjudication.</li>
                        <li>Fertility benefit manager coverage (Progyny, Maven, WIN, Carrot, etc.) may impact final costs.</li>
                        <li>Prior authorization requirements may affect coverage and reimbursement.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}


            {/* Payer-specific additional fields */}
            {payerConfig && (payerConfig.additionalFields || []).length > 0 && (
              <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="payer-additional-fields">
                <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                  <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>{payerConfig.payerName} — Additional Required Information</h2>
                  <p className={`text-xs ${t.textMuted} mt-1`}>These fields are specific to this payer's authorization workflow.</p>
                </div>
                <div className="p-6 grid grid-cols-2 gap-4">
                  {payerConfig.additionalFields.map(f => (
                    <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                      <Label className={`text-xs font-semibold ${t.label}`}>
                        {f.label} {f.required && <span className="text-red-500">*</span>}
                      </Label>
                      {f.type === "select" ? (
                        <Select
                          value={additionalFieldValues[f.key] || ""}
                          onValueChange={v => setAdditionalFieldValues({ ...additionalFieldValues, [f.key]: v })}
                        >
                          <SelectTrigger className={`mt-1 ${t.input}`} data-testid={`addl-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : f.type === "textarea" ? (
                        <Textarea
                          value={additionalFieldValues[f.key] || ""}
                          onChange={e => setAdditionalFieldValues({ ...additionalFieldValues, [f.key]: e.target.value })}
                          rows={3}
                          className={`mt-1 ${t.input}`}
                          data-testid={`addl-${f.key}`}
                          placeholder={f.helpText || ""}
                        />
                      ) : (
                        <Input
                          type={f.type === "number" ? "number" : "text"}
                          value={additionalFieldValues[f.key] || ""}
                          onChange={e => setAdditionalFieldValues({ ...additionalFieldValues, [f.key]: e.target.value })}
                          className={`mt-1 ${t.input}`}
                          data-testid={`addl-${f.key}`}
                          placeholder={f.helpText || ""}
                        />
                      )}
                      {f.helpText && <p className={`text-[11px] ${t.textMuted} mt-1`}>{f.helpText}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Expedited */}
            <div className={`${t.card} rounded-lg border shadow-sm p-6`}>
              <div className="flex items-start gap-3">
                <Checkbox checked={expeditedReview} onCheckedChange={setExpeditedReview} id="expedited" data-testid="expedited-review" />
                <label htmlFor="expedited" className={`text-sm ${t.text} cursor-pointer`}><span className="font-semibold">Expedited Review</span> — I certify that this request meets the definition of an urgent/expedited request.</label>
              </div>
            </div>
            {/* Service Description — Free-text + attachment, AI extracts codes */}
            <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="service-description-card">
              <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase flex items-center gap-2`}>
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Medical Necessity / Service Justification
                </h2>
                <p className={`text-xs ${t.textMuted} mt-1`}>
                  Describe the clinical encounter in your own words and/or attach supporting notes. Gemini will identify the most appropriate ICD-10 and CPT codes, then flag which procedures require prior authorization for <span className="font-semibold">{payer || "the selected payer"}</span>.
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <Label className={`text-xs font-semibold ${t.label}`}>
                    Clinical scenario / service description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    value={serviceText}
                    onChange={e => setServiceText(e.target.value)}
                    placeholder="e.g., 55-year-old male with type 2 diabetes, hypertension, hyperlipidemia. Annual physical with labs (A1C, lipid panel, CBC, CMP). Patient also has 3 months of left knee pain — ordering MRI left knee. Sleep study to evaluate suspected OSA."
                    rows={6}
                    className={`mt-1 ${t.input}`}
                    data-testid="service-description-textarea"
                  />
                  <p className={`text-[11px] ${t.textMuted} mt-1`}>
                    💡 Tip: include patient age/gender, chief complaint, relevant chronic conditions, and what services or tests you are ordering.
                  </p>
                </div>

                {/* File attach */}
                <div>
                  <Label className={`text-xs font-semibold ${t.label}`}>
                    Attach supporting document <span className={`text-[11px] ${t.textMuted}`}>(optional · clinical notes, CCDA, PDF, image)</span>
                  </Label>
                  <div className="mt-1 flex items-center gap-3">
                    <input
                      id="service-file-input"
                      type="file"
                      accept=".txt,.xml,.pdf,.docx,.png,.jpg,.jpeg"
                      className="hidden"
                      onChange={e => setServiceFile(e.target.files?.[0] || null)}
                      data-testid="service-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('service-file-input').click()}
                      className={t.btnOutline}
                      data-testid="attach-doc-btn"
                    >
                      <AttachIcon className="w-4 h-4 mr-1" /> Choose file
                    </Button>
                    {serviceFile && (
                      <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded border ${t.border}`}>
                        <FileText className="w-3.5 h-3.5" />
                        <span className={t.text}>{serviceFile.name}</span>
                        <button onClick={() => setServiceFile(null)} className="text-red-500 hover:text-red-400" data-testid="remove-file-btn">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 pt-2">
                  <p className={`text-xs ${t.textMuted}`}>
                    {analysisSummary
                      ? <><span className="font-semibold">AI summary:</span> {analysisSummary}</>
                      : "Gemini 3 Flash will extract codes and split them into PA-required vs PA-not-required tables below."}
                  </p>
                  <Button
                    onClick={analyzeClinicalText}
                    disabled={!serviceText || serviceText.trim().length < 5 || analyzing}
                    className={t.btn}
                    data-testid="generate-codes-btn"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {analyzing ? "Analyzing..." : "Analyze & Extract Codes"}
                  </Button>
                </div>

                {/* PA Classification summary cards */}
                {Object.keys(paClassification).length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2" data-testid="pa-classification-summary">
                    <div className={`p-3 rounded-lg border-l-4 border-l-red-500 ${isDark ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"} border`}>
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldAlert className="w-4 h-4 text-red-500" />
                        <span className={`text-xs font-semibold ${t.textStrong}`}>Prior Auth Required</span>
                      </div>
                      <div className="text-2xl font-bold text-red-500">
                        {Object.values(paClassification).filter(c => c.required === true).length}
                      </div>
                      <p className={`text-[11px] ${t.textMuted} mt-0.5`}>Must submit PA before service · per {payer || "payer"} policy</p>
                    </div>
                    <div className={`p-3 rounded-lg border-l-4 border-l-emerald-500 ${isDark ? "bg-emerald-500/10 border-emerald-500/20" : "bg-emerald-50 border-emerald-200"} border`}>
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="w-4 h-4 text-emerald-500" />
                        <span className={`text-xs font-semibold ${t.textStrong}`}>No PA Needed</span>
                      </div>
                      <div className="text-2xl font-bold text-emerald-500">
                        {Object.values(paClassification).filter(c => c.required === false).length}
                      </div>
                      <p className={`text-[11px] ${t.textMuted} mt-0.5`}>Routine / standard services · proceed directly</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Diagnosis Codes */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Diagnosis Code(s) {requiredMark("diagnosisCodes", true)}</h2>
                <span className={`text-[11px] ${t.textMuted}`}>Max {maxDiagnosis}</span>
              </div>
              <div className="p-6 space-y-3">
                {diagnosisCodes.length === 0 && (
                  <div className={`p-4 border-2 border-dashed rounded-md text-center ${t.border}`} data-testid="diagnosis-empty">
                    <p className={`text-xs ${t.textMuted}`}>No diagnosis codes yet — describe the clinical scenario above and click <strong className={t.textStrong}>Analyze & Extract Codes</strong>, or add manually.</p>
                  </div>
                )}
                {diagnosisCodes.map((diag, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Input value={diag.code} onChange={e => { const u = [...diagnosisCodes]; u[i].code = e.target.value; setDiagnosisCodes(u); searchDiagnosisCode(e.target.value); }} placeholder="ICD-10 code..." className={`flex-1 ${t.input}`} data-testid={`diag-code-${i}`} />
                    <Input value={diag.description} readOnly placeholder="Description" className={`flex-1 ${t.inputRo} text-sm`} />
                    <Button variant="ghost" size="sm" onClick={() => rmDiag(i)} className="text-red-500" data-testid={`rm-diag-${i}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
                {diagSearchResults.length > 0 && (
                  <div className={`${t.searchResult} border rounded shadow-lg max-h-40 overflow-y-auto`}>
                    {diagSearchResults.slice(0, 8).map((r, i) => (
                      <div key={i} className={`px-3 py-2 ${isDark ? "hover:bg-slate-700" : "hover:bg-blue-50"} cursor-pointer text-sm flex gap-2`} onClick={() => {
                        const idx = diagnosisCodes.findIndex(d => !d.code); const j = idx >= 0 ? idx : diagnosisCodes.length - 1;
                        const u = [...diagnosisCodes]; u[j] = { code: r.code, description: r.description }; setDiagnosisCodes(u); setDiagSearchResults([]);
                      }}><Badge variant="outline" className="font-mono text-xs">{r.code}</Badge><span className={t.text}>{r.description}</span></div>
                    ))}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={addDiag} disabled={diagnosisCodes.length >= maxDiagnosis} className={t.btnOutline} data-testid="add-diagnosis-btn"><Plus className="w-4 h-4 mr-1" /> Add diagnosis</Button>
              </div>
            </div>
            {/* Procedure Codes */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Procedure Code(s) {requiredMark("procedureCodes", true)}</h2>
                <span className={`text-[11px] ${t.textMuted}`}>Max {maxProcedures}</span>
              </div>
              <div className="p-6 space-y-3">
                {procedureCodes.length === 0 && (
                  <div className={`p-4 border-2 border-dashed rounded-md text-center ${t.border}`} data-testid="procedure-empty">
                    <p className={`text-xs ${t.textMuted}`}>No procedure codes yet — codes will appear here after you click <strong className={t.textStrong}>Analyze & Extract Codes</strong> above.</p>
                  </div>
                )}
                {procedureCodes.map((proc, i) => {
                  const paInfo = paClassification[proc.code];
                  const paBadge = paInfo && paInfo.status === "Review Required"
                    ? <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] flex items-center gap-1" title={paInfo.reason}><HelpCircle className="w-3 h-3" /> Review Required</Badge>
                    : paInfo && paInfo.required === true
                      ? <Badge className="bg-red-500 hover:bg-red-500 text-white text-[10px] flex items-center gap-1" title={paInfo.reason}><ShieldAlert className="w-3 h-3" /> PA Required</Badge>
                      : paInfo && paInfo.required === false
                        ? <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white text-[10px] flex items-center gap-1" title={paInfo.reason}><ShieldCheck className="w-3 h-3" /> No PA</Badge>
                        : null;
                  return (
                  <div key={i} className="flex items-center gap-3" data-testid={`proc-row-${i}`}>
                    <Select value={proc.type} onValueChange={v => { const u = [...procedureCodes]; u[i].type = v; setProcedureCodes(u); }}><SelectTrigger className={`w-32 ${t.input} text-xs`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="CPT/HCPCS">CPT/HCPCS</SelectItem><SelectItem value="ICD-10-PCS">ICD-10-PCS</SelectItem></SelectContent></Select>
                    <Input value={proc.code} onChange={e => { const u = [...procedureCodes]; u[i].code = e.target.value; setProcedureCodes(u); searchProcedureCode(e.target.value); }} placeholder="Code..." className={`flex-1 ${t.input}`} data-testid={`proc-code-${i}`} />
                    <Input value={proc.quantity} onChange={e => { const u = [...procedureCodes]; u[i].quantity = e.target.value; setProcedureCodes(u); }} placeholder="Qty" className={`w-20 ${t.input} text-center`} />
                    <Input value={proc.description} readOnly placeholder="Description" className={`flex-1 ${t.inputRo} text-sm`} />
                    {paBadge && <div data-testid={`pa-badge-${proc.code}`}>{paBadge}</div>}
                    <Button variant="ghost" size="sm" onClick={() => rmProc(i)} className="text-red-500" data-testid={`rm-proc-${i}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  );
                })}
                {procSearchResults.length > 0 && (
                  <div className={`${t.searchResult} border rounded shadow-lg max-h-40 overflow-y-auto`}>
                    {procSearchResults.slice(0, 8).map((r, i) => (
                      <div key={i} className={`px-3 py-2 ${isDark ? "hover:bg-slate-700" : "hover:bg-blue-50"} cursor-pointer text-sm flex gap-2`} onClick={() => {
                        const idx = procedureCodes.findIndex(p => !p.code); const j = idx >= 0 ? idx : procedureCodes.length - 1;
                        const u = [...procedureCodes]; u[j] = { ...u[j], code: r.code, description: r.description }; setProcedureCodes(u); setProcSearchResults([]);
                      }}><Badge variant="outline" className="font-mono text-xs">{r.code}</Badge><span className={t.text}>{r.description}</span></div>
                    ))}
                  </div>
                )}
                {procedureCodes.length < maxProcedures && <Button variant="outline" size="sm" onClick={addProc} className={t.btnOutline}><Plus className="w-4 h-4 mr-1" /> Add procedure</Button>}
              </div>
            </div>
          </div>
        )}

        {/* === STEP 3 — Rendering Provider (new spec) === */}
        {currentStep === 3 && (
          <Step3RenderingProvider
            t={t}
            isDark={isDark}
            paId={paId}
            payer={payer || "BCBSTX"}
            planType={(payerConfig?.planType) || "PPO"}
            selectedProvider={selectedRenderingProvider}
            initialConfirmation={renderingConfirmation}
            onProviderSelected={(p) => {
              setSelectedRenderingProvider(p);
              setRendProviderNpi(p.npi);
              setRendProviderName(p.display_name || `${p.first_name || ""} ${p.last_name || ""}`.trim());
              setRendFacilityName(p.is_facility ? p.organization : "");
              setUseReqProvider(false);
              // Auto-save so the eFax/email Send button is immediately enabled
              // (Step3 requires a paId before posting confirm-contact).
              autoSave();
            }}
            onConfirmationSent={(c) => setRenderingConfirmation(c)}
          />
        )}

        {/* === STEP 4: Payer-specific Clinical Documentation === */}
        {currentStep === 4 && (
          <div className="space-y-6" data-testid="step-4">
            <div className={`${t.mcgHeader} text-white rounded-lg p-4 flex items-center gap-4`} data-testid="clinical-questionnaire-header"><div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center"><ClipboardList className="w-5 h-5" /></div><div><h2 className="font-bold" data-testid="clinical-questionnaire-title">{payerConfig?.payerName ? `${payerConfig.payerName} Clinical Questionnaire` : "MCG Health Clinical Questionnaire"}</h2><p className="text-sm opacity-80">{payerConfig?.payerName ? `Payer-specific clinical criteria for ${payerConfig.payerName}` : "Document clinical indications for requested procedure codes"}</p></div>{mcgCompleted && <Badge className="ml-auto bg-green-500 text-white">Completed</Badge>}</div>
            {mcgStep === "disclaimer" && (
              <div className={`${t.card} rounded-lg border shadow-sm p-6`}>
                <h3 className={`font-semibold ${t.textStrong} mb-4`}>Disclaimer</h3>
                <div className={`${t.warnBg} border rounded-lg p-4 mb-4 text-sm ${t.text}`}><p className="mb-2">The clinical questionnaire collects clinical information supporting the authorization request.</p><p>Please ensure all clinical documentation accurately reflects the patient's current clinical status.</p></div>
                <div className="flex items-start gap-3 mb-4"><Checkbox checked={mcgDisclaimer} onCheckedChange={setMcgDisclaimer} id="mcg-disc" /><label htmlFor="mcg-disc" className={`text-sm ${t.text} cursor-pointer`}>I acknowledge that I have read and understand the disclaimer.</label></div>
                <Button onClick={() => setMcgStep("codes")} disabled={!mcgDisclaimer} className={t.btn} data-testid="mcg-proceed-btn">Proceed to Clinical Documentation</Button>
              </div>
            )}
            {mcgStep === "codes" && (
              <div className={`${t.card} rounded-lg border shadow-sm`}>
                <div className={`px-6 py-4 border-b ${t.cardHead}`}><h3 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Document Clinical Indications</h3></div>
                <div className="p-6">
                  <table className="w-full text-sm"><thead><tr className={`border-b ${t.border}`}><th className={`text-left py-2 ${t.label}`}>Code</th><th className={`text-left py-2 ${t.label}`}>Description</th><th className={`text-left py-2 ${t.label}`}>Qty</th><th className={`text-left py-2 ${t.label}`}>Status</th><th className="py-2"></th></tr></thead>
                    <tbody>{procedureCodes.filter(p => p.code).map((proc, i) => (
                      <tr key={i} className={`border-b ${t.border}`}><td className={`py-3 font-mono font-semibold ${t.accent}`}>{proc.code}</td><td className={`py-3 ${t.text}`}>{proc.description || "—"}</td><td className="py-3">{proc.quantity}</td><td className="py-3">{clinicalCriteria[proc.code] ? <Badge className="bg-green-500/20 text-green-500">Documented</Badge> : <Badge className="bg-amber-500/20 text-amber-500">Pending</Badge>}</td><td className="py-3"><Button size="sm" onClick={() => setMcgStep(`criteria-${proc.code}`)} className={t.btn}>Document Clinical</Button></td></tr>
                    ))}</tbody></table>
                  {procedureCodes.filter(p => p.code).every(p => clinicalCriteria[p.code]) && <Button onClick={() => { setMcgCompleted(true); setMcgStep("review"); }} className="mt-4 bg-green-600 hover:bg-green-500 text-white" data-testid="mcg-submit-btn"><Check className="w-4 h-4 mr-1" /> Submit Clinical Documentation</Button>}
                </div>
              </div>
            )}
            {mcgStep.startsWith("criteria-") && (
              <div className={`${t.card} rounded-lg border shadow-sm`}>
                <div className={`px-6 py-4 border-b ${t.cardHead}`}><h3 className={`text-sm font-bold ${t.cardHeadText}`}>Medical Policy Criteria — Code: <span className={t.accent}>{mcgStep.split("-")[1]}</span></h3></div>
                <div className="p-6">
                  <p className={`text-sm ${t.text} mb-4`}>Select all criteria that apply:</p>
                  <div className="space-y-3">{SAMPLE_CRITERIA.map((c, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${t.border} ${isDark ? "hover:bg-slate-800" : "hover:bg-blue-50"}`}><Checkbox id={`crit-${i}`} onCheckedChange={(checked) => { const code = mcgStep.split("-")[1]; const cur = clinicalCriteria[code] || []; setClinicalCriteria({ ...clinicalCriteria, [code]: checked ? [...cur, c] : cur.filter(x => x !== c) }); }} /><label htmlFor={`crit-${i}`} className={`text-sm ${t.text} cursor-pointer`}>{c}</label></div>
                  ))}</div>
                  <Separator className={`my-4 ${t.border}`} />
                  <h4 className={`text-xs font-bold ${t.label} uppercase mb-2`}>Additional Clinical Notes {requiredMark("clinicalNotes")}</h4>
                  <Textarea value={clinicalNotes} onChange={e => setClinicalNotes(e.target.value)} placeholder={`Enter supporting clinical information (${clinicalNotesLimit.toLocaleString()} char limit)`} maxLength={clinicalNotesLimit} className={t.input} rows={4} data-testid="clinical-notes" />
                  <p className={`text-xs ${t.textMuted} mt-1`}>{clinicalNotes.length}/{clinicalNotesLimit.toLocaleString()}</p>
                  <div className="flex gap-3 mt-4">
                    <Button variant="ghost" onClick={() => setMcgStep("codes")} className={t.btnGhost}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => { const code = mcgStep.split("-")[1]; if (!clinicalCriteria[code]?.length) setClinicalCriteria({ ...clinicalCriteria, [code]: ["Manual review"] }); setMcgStep("codes"); toast.success(`Saved for ${code}`); }} className={t.btn} data-testid="save-criteria-btn">Save</Button>
                  </div>
                </div>
              </div>
            )}
            {mcgStep === "review" && (<div className={`${t.successBg} border rounded-lg p-6 text-center`}><Check className={`w-12 h-12 ${t.successText} mx-auto mb-3`} /><h3 className={`font-bold ${t.successText} text-lg`}>Clinical Documentation Complete</h3><p className={`text-sm ${t.text} mt-1`}>All procedure codes documented. Click Next to proceed.</p></div>)}
          </div>
        )}

        {/* === STEP 5: Attachments + FHIR Auto-Pull + Gap Analysis === */}
        {currentStep === 5 && (
          <div className="space-y-6" data-testid="step-5">
            <div className={`${t.infoBg} border rounded-lg p-4 flex items-center gap-4 text-sm`}><User className={`w-5 h-5 ${t.infoText}`} /><span className={t.text}><strong className={t.textStrong}>{patientFirst} {patientLast}</strong> | Member ID: {memberId} | DOB: {patientDob}</span></div>

            {/* === Auto-pulled FHIR documents === */}
            <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="fhir-docs-card">
              <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                <div>
                  <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Patient Documentation (from EHR)</h2>
                  <p className={`text-xs ${t.textMuted} mt-1`}>
                    Auto-pulled from SMART on FHIR — labs, imaging, encounters, notes, and medications attached to this patient's chart.
                  </p>
                </div>
                <Button
                  onClick={fetchPatientDocuments}
                  disabled={docsLoading || !ehrPatientFhirId}
                  variant="outline"
                  size="sm"
                  className={t.btnOutline}
                  data-testid="refresh-fhir-docs-btn"
                >
                  {docsLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
                  {docsFetched ? "Refresh" : "Pull from EHR"}
                </Button>
              </div>
              <div className="p-6">
                {!ehrPatientFhirId && (
                  <div className={`p-4 border-2 border-dashed rounded text-center ${t.border}`}>
                    <p className={`text-xs ${t.textMuted}`}>Patient was not imported from EHR. Use manual upload below.</p>
                  </div>
                )}

                {docsLoading && (
                  <div className="text-center py-6">
                    <Loader2 className={`w-6 h-6 ${t.accent} animate-spin mx-auto mb-2`} />
                    <p className={`text-xs ${t.textMuted}`}>Reading patient chart from EHR...</p>
                  </div>
                )}

                {docsFetched && !docsLoading && fhirDocs.length > 0 && (
                  <div className="space-y-3">
                    <p className={`text-xs ${t.textMuted}`}>{fhirDocs.length} document(s) found. Uncheck any you don't want to attach.</p>
                    <div className="max-h-80 overflow-y-auto space-y-1 pr-1">
                      {fhirDocs.map(doc => (
                        <div key={doc.id} className={`flex items-start gap-3 p-2 rounded border ${t.border} ${isDark ? "bg-slate-900/40" : "bg-white"}`} data-testid={`fhir-doc-${doc.id}`}>
                          <Checkbox
                            checked={!!selectedFhirDocIds[doc.id]}
                            onCheckedChange={v => setSelectedFhirDocIds({ ...selectedFhirDocIds, [doc.id]: !!v })}
                            className="mt-1"
                          />
                          <Badge variant="outline" className={`text-[10px] uppercase mt-0.5 ${
                            doc.type === "lab" ? "text-cyan-500 border-cyan-500/30"
                            : doc.type === "imaging" ? "text-purple-500 border-purple-500/30"
                            : doc.type === "medication" ? "text-pink-500 border-pink-500/30"
                            : "text-emerald-500 border-emerald-500/30"
                          }`}>{doc.type}</Badge>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold ${t.textStrong} truncate`}>{doc.title}</p>
                            {doc.summary && <p className={`text-[11px] ${t.textMuted} truncate`}>{doc.summary}</p>}
                            <p className={`text-[10px] ${t.textMuted}`}>
                              {doc.date || "Unknown date"} · {doc.days_old > 365 ? `${Math.round(doc.days_old/365)}y old` : `${doc.days_old}d old`} · {doc.source}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {docsFetched && !docsLoading && fhirDocs.length === 0 && (
                  <div className={`p-4 border-2 border-dashed rounded text-center ${t.border}`}>
                    <p className={`text-xs ${t.textMuted}`}>No clinical documents found on this patient's FHIR record.</p>
                  </div>
                )}
              </div>
            </div>

            {/* === Documentation Gap Analysis === */}
            {gapAnalysis.length > 0 && (
              <div className={`${t.card} rounded-lg border shadow-sm`} data-testid="gap-analysis-card">
                <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                  <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>
                    {payerConfig?.payerName || payer} — Documentation Gap Analysis
                  </h2>
                  <p className={`text-xs ${t.textMuted} mt-1`}>
                    Comparing attached documents against {payerConfig?.payerName || payer}'s documentation requirements for prior authorization.
                  </p>
                </div>
                <div className="p-6 space-y-2">
                  {gapAnalysis.map(g => {
                    const cfg = {
                      met:           { color: "text-green-500 border-green-500/30",   bg: isDark ? "bg-green-950/30"   : "bg-green-50",  icon: <Check className="w-4 h-4 text-green-500" />, label: "Met" },
                      insufficient:  { color: "text-amber-500 border-amber-500/30",   bg: isDark ? "bg-amber-950/30"   : "bg-amber-50",  icon: <AlertCircle className="w-4 h-4 text-amber-500" />, label: "Insufficient" },
                      outdated:      { color: "text-orange-500 border-orange-500/30", bg: isDark ? "bg-orange-950/30" : "bg-orange-50", icon: <AlertCircle className="w-4 h-4 text-orange-500" />, label: "Outdated" },
                      missing:       { color: "text-red-500 border-red-500/30",       bg: isDark ? "bg-red-950/30"    : "bg-red-50",    icon: <X className="w-4 h-4 text-red-500" />, label: "Missing" },
                    }[g.status] || {};
                    return (
                      <div key={g.key} className={`flex items-start gap-3 p-3 rounded border ${t.border} ${cfg.bg}`} data-testid={`gap-${g.key}`}>
                        <div className="mt-0.5">{cfg.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm font-semibold ${t.textStrong}`}>
                              {g.label} {g.required && <span className="text-red-500">*</span>}
                            </p>
                            <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                          </div>
                          <p className={`text-xs ${t.textMuted} mt-1`} data-testid={`gap-${g.key}-detail`}>
                            {g.status === "met" && `${g.found_count} of ${g.min_count} required document(s) attached, within ${g.max_age_days} days.`}
                            {g.status === "insufficient" && `Only ${g.found_count} of ${g.min_count} required document(s) found. Need ${g.min_count - g.found_count} more.`}
                            {g.status === "outdated" && `${g.total_in_record} document(s) found in record but all are older than ${g.max_age_days} days. Need ${g.min_count} fresh document(s).`}
                            {g.status === "missing" && `No matching documents found. Required: ${g.min_count} document(s) within ${g.max_age_days} days.`}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {/* Summary banner */}
                  <div className={`mt-3 p-3 rounded border-2 ${
                    gapAnalysis.every(g => g.status === "met") ? "border-green-500/40 bg-green-500/10"
                    : gapAnalysis.some(g => g.required && (g.status === "missing" || g.status === "outdated")) ? "border-red-500/40 bg-red-500/10"
                    : "border-amber-500/40 bg-amber-500/10"
                  }`} data-testid="gap-summary">
                    <p className={`text-xs font-semibold ${t.textStrong}`}>
                      {gapAnalysis.every(g => g.status === "met")
                        ? "✓ All payer documentation requirements met — ready to submit"
                        : `${gapAnalysis.filter(g => g.status !== "met" && g.required).length} required requirement(s) unmet — upload missing documents below or proceed with risk of denial`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* === Manual Upload (fallback) === */}
            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead}`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Manual Upload</h2>
                <p className={`text-xs ${t.textMuted} mt-1`}>Fallback option — upload any additional documents not available in the EHR.</p>
              </div>
              <div className="p-6">
                <div className={`border-2 border-dashed ${t.dropzone} rounded-lg p-8 text-center transition-colors`}>
                  <Upload className={`w-10 h-10 ${t.textMuted} mx-auto mb-3`} /><p className={`text-sm ${t.text} mb-2`}>Drag and drop files here, or click to browse</p><p className={`text-xs ${t.textMuted}`}>Accepted: TIF, JPG, PDF, DOC, DOCX | Max: 60MB/file, 150MB total</p>
                  <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.tif,.tiff" className="hidden" id="file-upload" onChange={e => { setAttachments([...attachments, ...Array.from(e.target.files).map(f => ({ name: f.name, size: f.size }))]); toast.success(`${e.target.files.length} file(s) added`); }} />
                  <Button onClick={() => document.getElementById("file-upload").click()} className={`mt-3 ${t.btn}`} data-testid="add-files-btn"><Upload className="w-4 h-4 mr-1" /> Add files</Button>
                </div>
                {attachments.length > 0 && <div className="mt-4 space-y-2">{attachments.map((f, i) => (<div key={i} className={`flex items-center justify-between p-3 ${t.fileBg} rounded border`}><div className="flex items-center gap-2"><Paperclip className={`w-4 h-4 ${t.textMuted}`} /><span className={`text-sm ${t.text}`}>{f.name}</span><span className={`text-xs ${t.textMuted}`}>({(f.size / 1024).toFixed(1)} KB)</span></div><Button variant="ghost" size="sm" onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></div>))}</div>}
              </div>
            </div>
          </div>
        )}

        {/* === STEP 6: Review === */}
        {currentStep === 6 && (
          <div className="space-y-6" data-testid="step-6">
            {/* Status banner when viewing existing PA */}
            {paId && (
              <div className={`${isReadOnly ? "bg-amber-500/10 border-amber-500/40" : "bg-cyan-500/10 border-cyan-500/40"} border rounded-lg p-3 flex items-center justify-between`} data-testid="pa-status-banner">
                <div className="flex items-center gap-3">
                  <Badge className={
                    paStatus === "approved" ? "bg-green-500/20 text-green-500" :
                    paStatus === "denied" ? "bg-red-500/20 text-red-500" :
                    paStatus === "pending" ? "bg-amber-500/20 text-amber-500" :
                    "bg-cyan-500/20 text-cyan-500"
                  }>{(paStatus || "draft").toUpperCase()}</Badge>
                  <span className={`text-xs ${t.text}`}>
                    PA <span className="font-mono">{paId.slice(0, 12)}</span>
                    {isReadOnly && <> · <strong>Read-only</strong> — this PA is {paStatus.toLowerCase()} and cannot be edited.</>}
                    {!isReadOnly && <> · Editable</>}
                  </span>
                </div>
                <Button
                  onClick={downloadPdf}
                  variant="outline"
                  size="sm"
                  className={t.btnOutline}
                  data-testid="download-pdf-btn"
                >
                  <Printer className="w-4 h-4 mr-1" /> Download {payerConfig?.payerName ? `${payerConfig.payerName} ` : ""}PA PDF
                </Button>
              </div>
            )}

            <div className={`${t.card} rounded-lg border shadow-sm`}>
              <div className={`px-6 py-4 border-b ${t.cardHead} flex items-center justify-between`}>
                <h2 className={`text-sm font-bold ${t.cardHeadText} uppercase`}>Review Authorization Request</h2>
                <div className="flex gap-2">
                  {paId && (
                    <Button variant="ghost" size="sm" className={t.accent} onClick={downloadPdf} data-testid="review-print-pdf-btn">
                      <Printer className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className={t.accent} onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Print</Button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {/* 1) Member Information */}
                <div data-testid="review-member">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><User className="w-3.5 h-3.5" /> Member Information</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={t.textMuted}>Patient:</span> <strong className={t.textStrong}>{patientFirst} {patientLast}</strong></div>
                    <div><span className={t.textMuted}>Member ID:</span> <strong className={t.textStrong}>{memberId || "—"}</strong></div>
                    <div><span className={t.textMuted}>DOB:</span> <strong className={t.textStrong}>{patientDob || "—"}</strong></div>
                    <div><span className={t.textMuted}>Gender:</span> <strong className={t.textStrong}>{patientGender || "—"}</strong></div>
                    <div><span className={t.textMuted}>Relationship:</span> <strong className={t.textStrong}>{relationship || "—"}</strong></div>
                    <div><span className={t.textMuted}>Organization:</span> <strong className={t.textStrong}>{orgName || "—"}</strong></div>
                  </div>
                </div>
                <Separator className={t.border} />

                {/* 2) Payer / Authorization */}
                <div data-testid="review-payer">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><Shield className="w-3.5 h-3.5" /> Payer & Authorization</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={t.textMuted}>Payer:</span> <strong className={t.textStrong}>{payerConfig?.payerName || payer || "—"}</strong></div>
                    <div><span className={t.textMuted}>Payer ID:</span> <strong className={t.textStrong}>{payerConfig?.payerId || "—"}</strong></div>
                    <div><span className={t.textMuted}>Auth Type:</span> <strong className={t.textStrong}>{authType || "—"}</strong></div>
                    <div><span className={t.textMuted}>Urgency:</span> <strong className={t.textStrong}>{expeditedReview ? "Expedited" : "Routine"}</strong></div>
                    {payerConfig?.turnaroundDays && <div><span className={t.textMuted}>Turnaround:</span> <strong className={t.textStrong}>{expeditedReview ? payerConfig.turnaroundDays.expedited : payerConfig.turnaroundDays.standard} days</strong></div>}
                  </div>
                </div>
                <Separator className={t.border} />

                {/* 3) Eligibility */}
                {eligibilityData && (<><div data-testid="review-eligibility">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><Shield className="w-3.5 h-3.5" /> Eligibility & Coverage</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={t.textMuted}>Eligible:</span> <Badge className={eligibilityData.eligible ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}>{eligibilityData.eligible ? "Yes" : "No"}</Badge></div>
                    <div><span className={t.textMuted}>Plan:</span> <strong className={t.textStrong}>{eligibilityData.subscriber?.plan_name || eligibilityData.plan || "—"}</strong></div>
                    <div><span className={t.textMuted}>Status:</span> <strong className="text-green-500">{eligibilityData.plan_status || "—"}</strong></div>
                    <div><span className={t.textMuted}>Source:</span> <Badge variant="outline" className="text-xs">{eligibilityData.source}</Badge></div>
                  </div>
                </div><Separator className={t.border} /></>)}

                {/* 4) Requesting Provider */}
                <div data-testid="review-provider">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><Stethoscope className="w-3.5 h-3.5" /> Requesting Provider</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={t.textMuted}>Name:</span> <strong className={t.textStrong}>{reqProviderName || "—"}</strong></div>
                    <div><span className={t.textMuted}>NPI:</span> <strong className={t.textStrong}>{reqProviderNpi || "—"}</strong></div>
                    <div><span className={t.textMuted}>Tax ID:</span> <strong className={t.textStrong}>{reqProviderTaxId || "—"}</strong></div>
                    <div><span className={t.textMuted}>Specialty:</span> <strong className={t.textStrong}>{reqProviderSpecialty || "—"}</strong></div>
                    <div className="col-span-2"><span className={t.textMuted}>Address:</span> <strong className={t.textStrong}>{[reqProviderAddr, reqProviderCity, reqProviderState, reqProviderZip].filter(Boolean).join(", ") || "—"}</strong></div>
                  </div>
                </div>
                <Separator className={t.border} />

                {/* 5) Contact */}
                <div data-testid="review-contact">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><User className="w-3.5 h-3.5" /> Contact Information</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={t.textMuted}>Name:</span> <strong className={t.textStrong}>{contactName || "—"}</strong></div>
                    <div><span className={t.textMuted}>Phone:</span> <strong className={t.textStrong}>{contactPhone || "—"}{contactExt ? ` x${contactExt}` : ""}</strong></div>
                    <div><span className={t.textMuted}>Email:</span> <strong className={t.textStrong}>{contactEmail || "—"}</strong></div>
                    <div><span className={t.textMuted}>Fax:</span> <strong className={t.textStrong}>{contactFax || "—"}</strong></div>
                    <div><span className={t.textMuted}>Conf. Voicemail:</span> <strong className={t.textStrong}>{confidentialVoicemail || "—"}</strong></div>
                  </div>
                </div>
                <Separator className={t.border} />

                {/* 6) Service Information */}
                <div data-testid="review-service">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><ClipboardList className="w-3.5 h-3.5" /> Service Information</h3>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className={t.textMuted}>Service Type:</span> <strong className={t.textStrong}>{serviceType || "—"}</strong></div>
                    <div><span className={t.textMuted}>Place of Service:</span> <strong className={t.textStrong}>{placeOfService || "—"}</strong></div>
                    <div><span className={t.textMuted}>{isInpatient ? "Admission Date" : "From / To"}:</span> <strong className={t.textStrong}>{isInpatient ? `${admissionDate || "—"} (${admissionType || "—"})` : `${fromDate || "—"} → ${toDate || "—"}`}</strong></div>
                    {isInpatient && <div><span className={t.textMuted}>Level of Care:</span> <strong className={t.textStrong}>{levelOfCare || "—"}</strong></div>}
                  </div>
                </div>
                <Separator className={t.border} />

                {/* 7) Diagnoses */}
                <div data-testid="review-diagnoses">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><FileText className="w-3.5 h-3.5" /> Diagnosis Codes (ICD-10) — {diagnosisCodes.filter(d => d.code).length}</h3>
                  {diagnosisCodes.filter(d => d.code).length > 0 ? (
                    <div className="space-y-1">
                      {diagnosisCodes.filter(d => d.code).map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Badge variant="outline" className={`font-mono text-xs ${t.accent} border-current`}>{d.code}</Badge>
                          <span className={t.text}>{d.description || "—"}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className={`text-sm ${t.textMuted} italic`}>No diagnosis codes</p>}
                </div>
                <Separator className={t.border} />

                {/* 8) Procedures */}
                <div data-testid="review-procedures">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><FileText className="w-3.5 h-3.5" /> Procedure Codes (CPT/HCPCS) — {procedureCodes.filter(p => p.code).length}</h3>
                  {procedureCodes.filter(p => p.code).length > 0 ? (
                    <div className="space-y-1">
                      {procedureCodes.filter(p => p.code).map((p, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Badge variant="outline" className={`font-mono text-xs ${t.accent} border-current`}>{p.code}</Badge>
                          <span className={`text-xs ${t.textMuted}`}>{p.type}</span>
                          <span className={t.text}>{p.description || "—"}</span>
                          <span className={`text-xs ${t.textMuted}`}>· Qty: {p.quantity}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className={`text-sm ${t.textMuted} italic`}>No procedure codes</p>}
                </div>
                <Separator className={t.border} />

                {/* 9) Payer-specific Additional Fields */}
                {payerConfig && (payerConfig.additionalFields || []).some(f => additionalFieldValues[f.key]) && (<><div data-testid="review-additional">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><FileText className="w-3.5 h-3.5" /> {payerConfig.payerName} — Additional Information</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {payerConfig.additionalFields.filter(f => additionalFieldValues[f.key]).map(f => (
                      <div key={f.key}><span className={t.textMuted}>{f.label}:</span> <strong className={t.textStrong}>{additionalFieldValues[f.key]}</strong></div>
                    ))}
                  </div>
                </div><Separator className={t.border} /></>)}

                {/* 10) Rendering Provider */}
                <div data-testid="review-rendering">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><Building2 className="w-3.5 h-3.5" /> Rendering Provider</h3>
                  <p className={`text-sm ${t.text}`}>{useReqProvider ? "Same as requesting provider" : (rendProviderName ? `${rendProviderName} ${rendProviderNpi ? "· NPI " + rendProviderNpi : ""} ${rendFacilityName ? "· " + rendFacilityName : ""}` : "—")}</p>
                </div>
                <Separator className={t.border} />

                {/* 11) Clinical Notes */}
                <div data-testid="review-clinical-notes">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><FileText className="w-3.5 h-3.5" /> Clinical Justification</h3>
                  {clinicalNotes ? (
                    <div className={`text-sm ${t.text} whitespace-pre-line max-h-48 overflow-y-auto p-3 rounded border ${t.border} ${isDark ? "bg-slate-900/40" : "bg-gray-50"}`}>{clinicalNotes}</div>
                  ) : <p className={`text-sm ${t.textMuted} italic`}>No clinical notes provided</p>}
                </div>
                <Separator className={t.border} />

                {/* 12) Clinical Documentation (MCG / payer questionnaire) */}
                <div data-testid="review-questionnaire">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><ClipboardList className="w-3.5 h-3.5" /> {payerConfig?.payerName ? `${payerConfig.payerName} Clinical Questionnaire` : "MCG Clinical Documentation"}</h3>
                  {mcgCompleted
                    ? <Badge className="bg-green-500/20 text-green-500"><Check className="w-3 h-3 mr-1" /> Completed for {procedureCodes.filter(p => p.code).length} procedure code(s)</Badge>
                    : <Badge className="bg-amber-500/20 text-amber-500"><AlertCircle className="w-3 h-3 mr-1" /> Not completed</Badge>}
                </div>
                <Separator className={t.border} />

                {/* 13) Attachments + EHR docs */}
                <div data-testid="review-attachments">
                  <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><Paperclip className="w-3.5 h-3.5" /> Attachments</h3>
                  <div className="space-y-1 text-sm">
                    {fhirDocs.filter(d => selectedFhirDocIds[d.id]).length > 0 && (
                      <p className={`${t.textMuted}`}>
                        <strong className={t.textStrong}>{fhirDocs.filter(d => selectedFhirDocIds[d.id]).length}</strong> document(s) pulled from EHR ·{" "}
                        {Object.entries(
                          fhirDocs.filter(d => selectedFhirDocIds[d.id])
                                  .reduce((acc, d) => ({ ...acc, [d.type]: (acc[d.type] || 0) + 1 }), {})
                        ).map(([k, v]) => `${v} ${k}`).join(", ")}
                      </p>
                    )}
                    {attachments.length > 0 && (
                      <p className={t.textMuted}><strong className={t.textStrong}>{attachments.length}</strong> manual upload(s): {attachments.map(f => f.name).join(", ")}</p>
                    )}
                    {fhirDocs.filter(d => selectedFhirDocIds[d.id]).length === 0 && attachments.length === 0 && (
                      <p className={`${t.textMuted} italic`}>No attachments</p>
                    )}
                  </div>
                </div>

                {/* 14) Gap Analysis Summary */}
                {gapAnalysis.length > 0 && (<><Separator className={t.border} />
                  <div data-testid="review-gap-analysis">
                    <h3 className={`text-xs font-bold ${t.textMuted} uppercase mb-2 flex items-center gap-2`}><Shield className="w-3.5 h-3.5" /> Documentation Gap Analysis vs {payerConfig?.payerName || payer}</h3>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {gapAnalysis.map(g => (
                        <div key={g.key} className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-[10px] ${
                            g.status === "met" ? "text-green-500 border-green-500/30"
                            : g.status === "outdated" ? "text-orange-500 border-orange-500/30"
                            : g.status === "insufficient" ? "text-amber-500 border-amber-500/30"
                            : "text-red-500 border-red-500/30"
                          }`}>{g.status.toUpperCase()}</Badge>
                          <span className={`${t.text} truncate`}>{g.label}</span>
                        </div>
                      ))}
                    </div>
                  </div></>)}
              </div>
            </div>
          </div>
        )}

        </fieldset>

        {/* === STEP 7 — Approval Summary & Proposed Appointments === */}
        {currentStep === 7 && (
          <Step7ApprovalSummary t={t} isDark={isDark} paId={paId} autoLoadSlots={isReadOnly} />
        )}

        {/* Navigation */}
        <div className={`flex items-center justify-between mt-6 pt-4 border-t ${t.navBorder}`}>
          <Button variant="outline" onClick={() => currentStep === 1 ? navigate("/pa-requests") : handleBack()} className={`${t.border} ${t.btnGhost}`} data-testid="back-btn"><ChevronLeft className="w-4 h-4 mr-1" />{currentStep === 1 ? "Cancel" : "Back"}</Button>
          {isReadOnly ? (
            currentStep < 7 ? (
              <Button onClick={() => setCurrentStep(currentStep + 1)} className={`${t.btn} px-8`} data-testid="next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={downloadPdf} className={`${t.border} ${t.btnGhost} px-6`} data-testid="download-pdf-bottom-btn">
                  <FileText className="w-4 h-4 mr-1" /> Download PDF
                </Button>
                <Button onClick={() => navigate("/pa-requests")} className={`${t.btn} px-8`} data-testid="done-btn">
                  <Check className="w-4 h-4 mr-1" /> Done
                </Button>
              </div>
            )
          ) : currentStep < 6 ? (
            <Button onClick={handleNext} className={`${t.btn} px-8`} data-testid="next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : currentStep === 6 ? (
            <Button onClick={handleSubmit} disabled={submitting || isReadOnly} className="bg-green-600 hover:bg-green-500 text-white px-8" data-testid="submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              {isReadOnly ? "Read-only" : (paStatus && paStatus !== "draft" ? "Save Changes" : "Submit Authorization")}
            </Button>
          ) : (
            <Button onClick={() => navigate("/pa-requests")} className={`${t.btn} px-8`} data-testid="done-btn">
              <Check className="w-4 h-4 mr-1" /> Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
